import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Link, useParams, useLocation, Navigate } from 'react-router-dom';
import { getPlaygroundCourse, type PlaygroundLesson } from '../../data/playgroundData';
import { getCourseById } from '../../data/courses';
import { useProgress } from '../../hooks/useProgress';
import { fetchMyCohorts, type Cohort } from '../../services/cohortService';
import QuizPanel from '../../components/QuizPanel';
import type { User } from '../../services/authService';
import { trackVideoDecile } from '../../services/progressService';

type Tab = 'teoria' | 'ejercicio' | 'resultado-esperado' | 'solucion' | 'entrevista' | 'diccionario';

/* ── Single-pass syntax highlighters ── */

function highlightPython(code: string): string {
  const escaped = code.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const RE = new RegExp(
    [
      '("""[\\s\\S]*?"""|\'\'\'[\\s\\S]*?\'\'\'|"(?:[^"\\\\]|\\\\.)*"|\'(?:[^\'\\\\]|\\\\.)*\')',
      '(#[^\\n]*)',
      '\\b(def|class|return|if|elif|else|for|while|import|from|as|try|except|finally|with|raise|pass|break|continue|yield|lambda|and|or|not|in|is|global|nonlocal|assert|del)\\b',
      '\\b(print|range|len|type|int|float|str|bool|list|dict|set|tuple|input|open|enumerate|zip|map|filter|sorted|sum|min|max|abs|round|isinstance|hasattr|getattr|setattr|super|self|append)\\b',
      '\\b(True|False|None)\\b',
      '\\b(\\d+\\.?\\d*)\\b',
    ].join('|'),
    'g',
  );
  return escaped.replace(RE, (m, s, c, kw, bi, co, nu) => {
    if (s) return `<span class="tok-str">${m}</span>`;
    if (c) return `<span class="tok-comment">${m}</span>`;
    if (kw) return `<span class="tok-kw">${m}</span>`;
    if (bi) return `<span class="tok-builtin">${m}</span>`;
    if (co) return `<span class="tok-const">${m}</span>`;
    if (nu) return `<span class="tok-num">${m}</span>`;
    return m;
  });
}

function highlightSQL(code: string): string {
  const escaped = code.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const RE = new RegExp(
    [
      "('(?:[^'\\\\]|\\\\.)*')",                                                        // strings
      '(--[^\\n]*)',                                                                     // comments
      '\\b(SELECT|FROM|WHERE|JOIN|INNER|LEFT|RIGHT|FULL|OUTER|CROSS|ON|AND|OR|NOT|IN|EXISTS|BETWEEN|LIKE|IS|NULL|CASE|WHEN|THEN|ELSE|END|AS|UNION|ALL|INSERT|INTO|UPDATE|DELETE|SET|CREATE|ALTER|DROP|TABLE|VIEW|INDEX|SCHEMA|DATABASE|IF|REPLACE|TEMPORARY|TEMP|WITH|RECURSIVE|HAVING|GROUP|BY|ORDER|ASC|DESC|LIMIT|OFFSET|DISTINCT|OVER|PARTITION|ROWS|RANGE|UNBOUNDED|PRECEDING|FOLLOWING|CURRENT|ROW|VALUES|MERGE|USING|MATCHED|OVERWRITE)\\b',
      '\\b(COUNT|SUM|AVG|MIN|MAX|ROUND|COALESCE|NULLIF|CAST|CONCAT|UPPER|LOWER|TRIM|LENGTH|SUBSTRING|DATE|YEAR|MONTH|DAY|ROW_NUMBER|RANK|DENSE_RANK|LAG|LEAD|FIRST_VALUE|LAST_VALUE|NTILE|PERCENT_RANK|CUME_DIST|PIVOT|UNPIVOT)\\b',
      '\\b(\\d+\\.?\\d*)\\b',                                                            // numbers
    ].join('|'),
    'gi',
  );
  return escaped.replace(RE, (m, s, c, kw, fn, nu) => {
    if (s) return `<span class="tok-str">${m}</span>`;
    if (c) return `<span class="tok-comment">${m}</span>`;
    if (kw) return `<span class="tok-kw">${m.toUpperCase()}</span>`;
    if (fn) return `<span class="tok-builtin">${m.toUpperCase()}</span>`;
    if (nu) return `<span class="tok-num">${m}</span>`;
    return m;
  });
}

function highlightText(code: string): string {
  return code.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function highlight(code: string, lang: 'python' | 'sql' | 'text'): string {
  if (lang === 'text') return highlightText(code);
  return lang === 'sql' ? highlightSQL(code) : highlightPython(code);
}

function renderMarkdownInline(s: string): string {
  let r = s.replace(/\*\*(.*?)\*\*/g, '<strong class="text-white font-semibold">$1</strong>');
  r = r.replace(/`([^`]+)`/g, '<code class="rounded bg-gray-800/80 px-1.5 py-0.5 text-green-400 text-xs font-mono">$1</code>');
  r = r.replace(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-green-400 underline hover:text-green-300">$1</a>');
  return r;
}

/* ── Markdown-lite renderer for theory content ── */
function renderMarkdown(text: string): JSX.Element[] {
  const lines = text.split('\n');
  const elements: JSX.Element[] = [];
  let inCodeBlock = false;
  let codeLines: string[] = [];
  let codeBlockStart = 0;

  const applyInline = (s: string): string => {
    let r = s.replace(/\*\*(.*?)\*\*/g, '<strong class="text-white font-semibold">$1</strong>');
    r = r.replace(/`([^`]+)`/g, '<code class="rounded bg-gray-800/80 px-1.5 py-0.5 text-green-400 text-xs font-mono">$1</code>');
    r = r.replace(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-green-400 underline hover:text-green-300">$1</a>');
    r = r.replace(/(^|[^"'>])(https?:\/\/[^\s<]+)/g, '$1<a href="$2" target="_blank" rel="noopener noreferrer" class="text-green-400 underline hover:text-green-300">$2</a>');
    return r;
  };

  const youtubeId = (url: string): string | null => {
    const m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/);
    return m ? m[1] : null;
  };

  const isTableSep = (l: string) => /^\|[\s\-:|]+\|$/.test(l.trim());
  const isTableRow = (l: string) => l.trim().startsWith('|') && l.trim().endsWith('|');

  const parseTableRow = (l: string): string[] =>
    l.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map(c => c.trim());

  let i = 0;
  while (i < lines.length) {
    const line = lines[i];

    if (line.startsWith('```')) {
      if (inCodeBlock) {
        elements.push(
          <pre key={`code-${codeBlockStart}`} className="rounded-lg bg-black/60 border border-gray-700 px-4 py-3 text-sm font-mono text-green-400 overflow-x-auto leading-relaxed">
            {codeLines.join('\n')}
          </pre>
        );
        codeLines = [];
        inCodeBlock = false;
      } else {
        inCodeBlock = true;
        codeBlockStart = i;
      }
      i++;
      continue;
    }

    if (inCodeBlock) {
      codeLines.push(line);
      i++;
      continue;
    }

    if (isTableRow(line) && i + 1 < lines.length && isTableSep(lines[i + 1])) {
      const headers = parseTableRow(line);
      i += 2;
      const rows: string[][] = [];
      while (i < lines.length && isTableRow(lines[i]) && !isTableSep(lines[i])) {
        rows.push(parseTableRow(lines[i]));
        i++;
      }
      elements.push(
        <div key={`tbl-${i}`} className="overflow-x-auto rounded-lg border border-gray-700 my-2">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-800/80 border-b border-gray-700">
                {headers.map((h, hi) => (
                  <th key={hi} className="px-3 py-2 text-left text-xs font-bold text-green-400 whitespace-nowrap" dangerouslySetInnerHTML={{ __html: applyInline(h) }} />
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, ri) => (
                <tr key={ri} className={`border-b border-gray-800 ${ri % 2 === 0 ? 'bg-[#0d1117]' : 'bg-[#161b22]'}`}>
                  {row.map((cell, ci) => (
                    <td key={ci} className="px-3 py-1.5 text-gray-300 whitespace-nowrap" dangerouslySetInnerHTML={{ __html: applyInline(cell) }} />
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      continue;
    }

    if (line.startsWith('### ')) {
      elements.push(<h4 key={i} className="text-sm font-bold text-blue-400 mt-3 mb-1" dangerouslySetInnerHTML={{ __html: applyInline(line.slice(4)) }} />);
    } else if (line.startsWith('## ')) {
      elements.push(<h3 key={i} className="text-base font-bold text-green-400 mt-4 mb-1" dangerouslySetInnerHTML={{ __html: applyInline(line.slice(3)) }} />);
    } else if (line.startsWith('# ')) {
      elements.push(<h2 key={i} className="text-lg font-bold text-white mt-4 mb-2" dangerouslySetInnerHTML={{ __html: applyInline(line.slice(2)) }} />);
    } else if (/^\d+\.\s/.test(line)) {
      const content = line.replace(/^\d+\.\s/, '');
      elements.push(
        <div key={i} className="flex items-start gap-2 ml-1">
          <span className="text-green-400 font-bold text-sm flex-shrink-0">{line.match(/^\d+/)![0]}.</span>
          <span dangerouslySetInnerHTML={{ __html: applyInline(content) }} />
        </div>
      );
    } else if (line.startsWith('- ')) {
      elements.push(
        <div key={i} className="flex items-start gap-2 ml-1">
          <span className="mt-2 h-1.5 w-1.5 rounded-full bg-green-500/60 flex-shrink-0" />
          <span dangerouslySetInnerHTML={{ __html: applyInline(line.slice(2)) }} />
        </div>
      );
    } else if (line.trim() === '') {
      elements.push(<div key={i} className="h-1" />);
    } else if (youtubeId(line.trim())) {
      const vid = youtubeId(line.trim())!;
      elements.push(
        <div key={i} className="my-3 rounded-lg overflow-hidden border border-gray-700" style={{ aspectRatio: '16/9', maxWidth: 560 }}>
          <iframe src={`https://www.youtube.com/embed/${vid}?modestbranding=1&rel=0&vq=hd1080`} title="Video" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="w-full h-full" />
        </div>
      );
    } else {
      elements.push(<p key={i} dangerouslySetInnerHTML={{ __html: applyInline(line) }} />);
    }
    i++;
  }

  return elements;
}

/* ── Print-friendly theory export ── */
function markdownToPlainHtml(text: string): string {
  let r = text;
  r = r.replace(/^### (.+)$/gm, '<h4 style="margin:12px 0 4px;font-size:13px;color:#333;">$1</h4>');
  r = r.replace(/^## (.+)$/gm, '<h3 style="margin:16px 0 6px;font-size:15px;color:#222;">$1</h3>');
  r = r.replace(/^# (.+)$/gm, '<h2 style="margin:18px 0 8px;font-size:17px;color:#111;">$1</h2>');
  r = r.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  r = r.replace(/`([^`]+)`/g, '<code style="background:#f3f3f3;padding:1px 4px;border-radius:3px;font-size:12px;">$1</code>');
  r = r.replace(/^- (.+)$/gm, '<li style="margin-left:16px;list-style:disc;">$1</li>');
  r = r.replace(/^\d+\.\s(.+)$/gm, '<li style="margin-left:16px;list-style:decimal;">$1</li>');
  r = r.replace(/^(?!<[hlu])((?!<li).+)$/gm, '<p style="margin:4px 0;">$1</p>');
  r = r.replace(/^```[\s\S]*?^```/gm, (m) => {
    const code = m.replace(/^```\w*\n?/, '').replace(/\n?```$/, '');
    return `<pre style="background:#f5f5f5;padding:8px 12px;border-radius:4px;font-size:12px;overflow-x:auto;border:1px solid #ddd;">${code}</pre>`;
  });
  return r;
}

function openTheoryPrintView(courseName: string, lessons: { level: string; theory: { heading: string; intro: string; sections: { title: string; body: string }[] } }[]) {
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${courseName} — Teoría</title>
<style>body{font-family:Georgia,serif;max-width:720px;margin:40px auto;padding:0 20px;color:#222;font-size:14px;line-height:1.6}
h1{font-size:22px;border-bottom:2px solid #222;padding-bottom:8px}
.lesson{page-break-inside:avoid;margin-bottom:32px;border-bottom:1px solid #ccc;padding-bottom:24px}
.lesson-title{font-size:17px;margin:0 0 4px;color:#111}
.lesson-level{font-size:11px;color:#666;text-transform:uppercase;letter-spacing:1px}
.intro{color:#555;font-style:italic;margin:8px 0 12px}
.section{margin:12px 0;padding:12px;background:#fafafa;border-radius:4px;border:1px solid #eee}
.section h3{margin:0 0 6px;font-size:14px;color:#333}
@media print{body{margin:20px;font-size:12px}.lesson{page-break-inside:avoid}}
</style></head><body>
<h1>${courseName}</h1>
<p style="color:#666;margin-bottom:24px;">Material teórico completo — ${lessons.length} lecciones</p>
${lessons.map((l, i) => `<div class="lesson">
<span class="lesson-level">${l.level}</span>
<h2 class="lesson-title">Lección ${i + 1}: ${l.theory.heading}</h2>
<p class="intro">${l.theory.intro}</p>
${l.theory.sections.map(s => `<div class="section"><h3>${s.title}</h3>${markdownToPlainHtml(s.body)}</div>`).join('')}
</div>`).join('')}
</body></html>`;
  const w = window.open('', '_blank');
  if (w) { w.document.write(html); w.document.close(); w.print(); }
}

/* ── Editor language labels ── */
const LANG_LABEL: Record<string, { name: string; icon: JSX.Element }> = {
  python: {
    name: 'Editor Python',
    icon: <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 24 24" fill="currentColor"><path d="M14.25.18l.9.2.73.26.59.3.45.32.34.34.25.34.16.33.1.3.04.26.02.2-.01.13V8.5l-.05.63-.13.55-.21.46-.26.38-.3.31-.33.25-.35.19-.35.14-.33.1-.3.07-.26.04-.21.02H8.77l-.69.05-.59.14-.5.22-.41.27-.33.32-.27.35-.2.36-.15.37-.1.35-.07.32-.04.27-.02.21v3.06H3.17l-.21-.03-.28-.07-.32-.12-.35-.18-.36-.26-.36-.36-.35-.46-.32-.59-.28-.73-.21-.88-.14-1.05L0 11.97l.06-1.22.16-1.04.24-.87.32-.71.36-.57.4-.44.42-.33.42-.24.4-.16.36-.1.32-.05.24-.01h.16l.06.01h8.16v-.83H6.18l-.01-2.75-.02-.37.05-.34.11-.31.17-.28.25-.26.31-.23.38-.2.44-.18.49-.14.55-.1.59-.06.65-.04.69-.01.73.03.77.06.82.1.87.13.92.18.96.22.47.13.45.12.44.13L14.25.18zM9.37 15.5l.05.64.13.55.21.46.26.38.3.31.33.25.35.19.35.14.33.1.3.07.26.04.21.02.13-.01h5.84l.69-.05.59-.14.5-.22.41-.27.33-.32.27-.35.2-.36.15-.37.1-.35.07-.32.04-.27.02-.21V12h-2.89l-.01.61-.04.42-.08.31-.13.25-.19.2-.26.18-.31.13-.36.09-.41.07-.44.04-.47.02H9.36zM20.83 11.97l.06 1.22-.16 1.04-.24.86-.32.71-.36.57-.4.45-.42.33-.42.24-.4.16-.36.09-.32.05-.24.02-.16-.01h-8.16v.83h5.18l.01 2.76.02.36-.05.34-.11.31-.17.29-.25.25-.31.24-.38.2-.44.17-.49.15-.55.09-.59.06-.65.04-.69.01-.73-.03-.77-.06-.82-.1-.87-.14-.92-.17-.96-.22-.47-.13-.45-.13-.44-.12-.14-.04.9-.2.73-.25.59-.3.45-.33.34-.34.25-.34.16-.33.1-.3.04-.25.02-.2-.01-.13v-5.34l.05-.64.13-.55.21-.46.26-.38.3-.31.33-.25.35-.19.35-.14.33-.1.3-.07.26-.04.21-.02.13.01z" /></svg>,
  },
  sql: {
    name: 'Editor SQL',
    icon: <svg className="h-5 w-5 text-blue-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}><ellipse cx="12" cy="6" rx="9" ry="3" /><path d="M3 6v6c0 1.657 4.03 3 9 3s9-1.343 9-3V6" /><path d="M3 12v6c0 1.657 4.03 3 9 3s9-1.343 9-3v-6" /></svg>,
  },
  text: {
    name: 'Bloc de notas',
    icon: <svg className="h-5 w-5 text-purple-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}><path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>,
  },
};

/* ════════════════════════════════════════════════════════════
   YouTube Player with progress tracking (IFrame API)
   ════════════════════════════════════════════════════════════ */
declare global { interface Window { YT: any; onYouTubeIframeAPIReady: (() => void) | undefined; } }

function YouTubePlayer({ url, title, courseId: cId, lessonIndex, onProgress80 }: {
  url: string; title: string; courseId: string; lessonIndex: number; onProgress80: () => void;
}) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<any>(null);
  const firedRef = useRef(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const onProgress80Ref = useRef(onProgress80);
  onProgress80Ref.current = onProgress80;
  const reachedDecilesRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    firedRef.current = false;
    reachedDecilesRef.current = new Set();
    const videoId = url.match(/embed\/([\w-]+)/)?.[1];
    if (!videoId || !wrapperRef.current) return;

    const playerDiv = document.createElement('div');
    playerDiv.style.width = '100%';
    playerDiv.style.height = '100%';
    wrapperRef.current.innerHTML = '';
    wrapperRef.current.appendChild(playerDiv);

    const createPlayer = () => {
      playerRef.current = new window.YT.Player(playerDiv, {
        videoId,
        playerVars: { modestbranding: 1, rel: 0, showinfo: 0, iv_load_policy: 3, vq: 'hd1080' },
        width: '100%',
        height: '100%',
        events: {
          onStateChange: (e: any) => {
            if (e.data === window.YT.PlayerState.PLAYING && !intervalRef.current) {
              intervalRef.current = setInterval(() => {
                if (!playerRef.current) return;
                const current = playerRef.current.getCurrentTime?.();
                const duration = playerRef.current.getDuration?.();
                if (!duration || duration <= 0) return;
                const pct = current / duration;
                const currentDecile = Math.floor(pct * 10) * 10;
                for (let d = 10; d <= currentDecile && d <= 100; d += 10) {
                  if (!reachedDecilesRef.current.has(d)) {
                    reachedDecilesRef.current.add(d);
                    trackVideoDecile(cId, lessonIndex, d);
                  }
                }
                if (!firedRef.current && pct >= 0.8) {
                  firedRef.current = true;
                  onProgress80Ref.current();
                }
              }, 30000);
            }
            if (e.data === window.YT.PlayerState.PAUSED || e.data === window.YT.PlayerState.ENDED) {
              if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
            }
          },
        },
      });
    };

    if (window.YT?.Player) {
      createPlayer();
    } else {
      const prev = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => { prev?.(); createPlayer(); };
      if (!document.querySelector('script[src*="youtube.com/iframe_api"]')) {
        const tag = document.createElement('script');
        tag.src = 'https://www.youtube.com/iframe_api';
        document.head.appendChild(tag);
      }
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      playerRef.current?.destroy?.();
      playerRef.current = null;
      if (wrapperRef.current) wrapperRef.current.innerHTML = '';
    };
  }, [url, cId, lessonIndex]);

  return <div ref={wrapperRef} className="h-full w-full" title={title} />;
}

/* ════════════════════════════════════════════════════════════
   Component
   ════════════════════════════════════════════════════════════ */
export default function LessonPlayground({ user }: { user?: User | null }) {
  const { courseId, cohortCode } = useParams<{ courseId: string; cohortCode?: string }>();
  const location = useLocation();
  const isBootcampRoute = location.pathname.startsWith('/bootcamps');
  const course = getCourseById(courseId || '');

  const [cohort, setCohort] = useState<Cohort | null>(null);
  const [redirectCohort, setRedirectCohort] = useState<string | null>(null);
  const [cohortDenied, setCohortDenied] = useState(false);
  const [cohortLoading, setCohortLoading] = useState(isBootcampRoute);

  useEffect(() => {
    if (!isBootcampRoute) return;
    setCohortLoading(true);
    fetchMyCohorts().then(cohorts => {
      if (cohortCode) {
        const match = cohorts.find(c => c.cohort_code === cohortCode);
        if (match) { setCohort(match); setCohortDenied(false); }
        else setCohortDenied(true);
      } else if (cohorts.length > 0) {
        const match = cohorts.find(c => c.bootcamp_id === courseId);
        if (match) setRedirectCohort(match.cohort_code);
      }
    }).finally(() => setCohortLoading(false));
  }, [cohortCode, courseId, isBootcampRoute]);

  const cohortFlags = cohort ? { weeksTheoricUnlocked: cohort.weeks_theoric_unlocked, weeksGroupUnlocked: cohort.weeks_group_unlocked, startsAt: cohort.starts_at, cohortCode: cohort.cohort_code } : undefined;
  const pgCourse = cohortLoading ? null : getPlaygroundCourse(courseId || '', cohortFlags);

  const [activeTab, setActiveTab] = useState<Tab>('teoria');
  const [currentIdx, setCurrentIdx] = useState(0);
  const [videoCollapsed, setVideoCollapsed] = useState(false);
  const [quizCollapsed, setQuizCollapsed] = useState(false);
  const [videoTheater, setVideoTheater] = useState(false);
  const [videoTab, setVideoTab] = useState<'teorica' | 'grupal'>('teorica');
  const [derExpanded, setDerExpanded] = useState(false);
  const [code, setCode] = useState('');
  const [output, setOutput] = useState('');
  const [sqlTableData, setSqlTableData] = useState<{ columns: string[]; rows: Record<string, any>[]; totalRows: number } | null>(null);
  const [showOutput, setShowOutput] = useState(false);
  const [copied, setCopied] = useState(false);
  const [loadedSolution, setLoadedSolution] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [validation, setValidation] = useState<null | { passed: boolean; details: string[] }>(null);
  const [running, setRunning] = useState(false);
  const [outputHeight, setOutputHeight] = useState(208);
  const [expectedResult, setExpectedResult] = useState<{ columns: string[]; rows: Record<string, any>[]; totalRows: number } | null>(null);
  const [expectedLoading, setExpectedLoading] = useState(false);
  const [expectedError, setExpectedError] = useState<string | null>(null);
  const [schemaTables, setSchemaTables] = useState<{ name: string; columns: { name: string; type: string }[] }[]>([]);
  const [expandedSchemaTables, setExpandedSchemaTables] = useState<Set<string>>(new Set());
  const [schemaLoading, setSchemaLoading] = useState(false);
  const [showSchema, setShowSchema] = useState(true);
  const [userRanking, setUserRanking] = useState<number[]>([]);
  const [rankingSubmitted, setRankingSubmitted] = useState(false);
  const [shuffledOptions, setShuffledOptions] = useState<{ id: number; text: string }[]>([]);
  const [showExerciseNav, setShowExerciseNav] = useState(false);
  const [quizResetKey, setQuizResetKey] = useState(0);
  const [showMetricsNudge, setShowMetricsNudge] = useState(false);
  const { completedLessons, resumeIndex, loading: progressLoading, markCompleted, resetLesson, percentComplete } = useProgress(courseId);
  const isDraggingRef = useRef(false);
  const dragStartYRef = useRef(0);
  const dragStartHeightRef = useRef(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const highlightRef = useRef<HTMLPreElement>(null);

  const syncScroll = useCallback(() => {
    if (textareaRef.current && highlightRef.current) {
      highlightRef.current.scrollTop = textareaRef.current.scrollTop;
      highlightRef.current.scrollLeft = textareaRef.current.scrollLeft;
    }
  }, []);

  const handleMarkCompleted = useCallback((idx: number, savedCode?: string) => {
    markCompleted(idx, savedCode);
    if (isBootcampRoute && !completedLessons.has(idx)) {
      setShowMetricsNudge(true);
    }
  }, [markCompleted, isBootcampRoute, completedLessons]);

  // Load database schema for SQL courses
  useEffect(() => {
    if (!pgCourse || pgCourse.language !== 'sql') return;
    const token = localStorage.getItem('auth_token');
    if (!token) return;

    setSchemaLoading(true);
    const isProduction = window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1';
    const baseUrl = isProduction ? '/api/sql/tables.php' : 'http://localhost:8080/api/sql/tables.php';
    const dbName = pgCourse.database || 'default';
    const url = `${baseUrl}?database=${dbName}`;

    (async () => {
      try {
        let response = await fetch(url, {
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        });
        if (response.status === 401) {
          const fb = `${baseUrl}?database=${dbName}&token=${encodeURIComponent(token)}`;
          response = await fetch(fb, { headers: { 'Content-Type': 'application/json' } });
        }
        const contentType = response.headers.get('content-type') || '';
        if (!contentType.includes('application/json')) return;
        const data = await response.json();
        if (data.success && data.tables) {
          setSchemaTables(data.tables);
        }
      } catch { /* silently fail */ }
      finally { setSchemaLoading(false); }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pgCourse?.courseId]);

  const toggleSchemaTable = (name: string) => {
    setExpandedSchemaTables(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  };

  // Initialize code + resume position on first render
  useEffect(() => {
    if (!initialized && pgCourse && pgCourse.lessons.length > 0 && !progressLoading) {
      setInitialized(true);
      let startIdx = resumeIndex != null && resumeIndex < pgCourse.lessons.length ? resumeIndex : 0;
      // Clamp to last available lesson so resume can't bypass time-locked weeks
      const now = new Date();
      while (startIdx > 0) {
        const af = pgCourse.lessons[startIdx].availableFrom;
        if (!af || now >= new Date(af + 'T00:00:00')) break;
        startIdx--;
      }
      setCurrentIdx(startIdx);
      setCode(pgCourse.lessons[startIdx].starterCode);
    }
  }, [initialized, pgCourse, currentIdx, progressLoading, resumeIndex]);

  // Auto-save when exercise is completed
  useEffect(() => {
    if (validation?.passed) {
      handleMarkCompleted(currentIdx, code);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validation?.passed, currentIdx]);

  // Shuffle ranking options once per lesson so correct order isn't obvious
  useEffect(() => {
    if (!pgCourse) return;
    const r = pgCourse.lessons[currentIdx]?.ranking;
    if (r) {
      const opts = [...r.options];
      for (let i = opts.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [opts[i], opts[j]] = [opts[j], opts[i]];
      }
      setShuffledOptions(opts);
    } else {
      setShuffledOptions([]);
    }
  }, [currentIdx, pgCourse]);

  useEffect(() => {
    if (!derExpanded) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setDerExpanded(false); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [derExpanded]);

  useEffect(() => {
    if (!videoTheater) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setVideoTheater(false); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [videoTheater]);

  // Redirect if user is not enrolled in this cohort
  const cohortSegment = cohortCode ? `/${cohortCode}` : '';

  if (cohortLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-[#0d1117]">
        <div className="w-10 h-10 border-4 border-green-500/30 border-t-green-400 rounded-full animate-spin" />
      </div>
    );
  }

  if (cohortDenied && !user?.is_admin) {
    return <Navigate to="/bootcamps" replace />;
  }

  if (isBootcampRoute && !cohortCode && redirectCohort) {
    return <Navigate to={`/bootcamps/${courseId}/${redirectCohort}/playground`} replace />;
  }

  if (course?.category === 'bootcamp' && !isBootcampRoute) {
    return <Navigate to={`/bootcamps/${courseId}${cohortSegment}/playground`} replace />;
  }

  // Redirect if course not found — AFTER all hooks
  if (!pgCourse) return <Navigate to={isBootcampRoute ? '/bootcamps' : '/cursos'} replace />;

  // Empty playground — show "coming soon" state
  if (pgCourse.lessons.length === 0) {
    return (
      <div className="flex h-screen flex-col items-center justify-center bg-[#0d1117] text-white">
        <div className="flex flex-col items-center gap-6 max-w-md text-center">
          <div className="flex h-20 w-20 items-center justify-center rounded-full border-2 border-yellow-500/30 bg-yellow-500/10">
            <svg className="h-9 w-9 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
              <path d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-white">Próximamente</h2>
          <p className="text-sm text-gray-400 leading-relaxed">
            El playground interactivo de <span className="text-green-400 font-semibold">{pgCourse.courseTitle}</span> está en construcción. Pronto vas a poder practicar directamente desde acá.
          </p>
          <Link
            to={isBootcampRoute ? `/bootcamps/${pgCourse.courseId}${cohortSegment}` : `/cursos/${pgCourse.courseId}`}
            className="flex items-center gap-2 rounded-lg bg-green-600 px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-green-500"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path d="M15 19l-7-7 7-7" /></svg>
            Volver al curso
          </Link>
        </div>
      </div>
    );
  }

  const lesson: PlaygroundLesson = pgCourse.lessons[currentIdx];
  const totalLessons = pgCourse.lessons.length;
  const lang = pgCourse.language;
  const langInfo = LANG_LABEL[lang] || LANG_LABEL.python;

  const isLessonAvailable = (idx: number): boolean => {
    if (user?.is_admin) return true;
    const l = pgCourse.lessons[idx];
    if (!l?.availableFrom) return true;
    const now = new Date();
    const release = new Date(l.availableFrom + 'T00:00:00');
    return now >= release;
  };

  const goToLesson = (idx: number) => {
    if (!isLessonAvailable(idx)) return;
    setCurrentIdx(idx);
    setCode(pgCourse.lessons[idx].starterCode);
    setActiveTab('teoria');
    window.scrollTo({ top: 0 });
    setShowOutput(false);
    setLoadedSolution(false);
    setValidation(null);
    setSqlTableData(null);
    setExpectedResult(null);
    setExpectedError(null);
    setUserRanking([]);
    setRankingSubmitted(false);
  };

  const handleResetLesson = () => {
    resetLesson(currentIdx);
    setCode(pgCourse.lessons[currentIdx].starterCode);
    setOutput('');
    setShowOutput(false);
    setLoadedSolution(false);
    setValidation(null);
    setSqlTableData(null);
    setExpectedResult(null);
    setExpectedError(null);
    setUserRanking([]);
    setRankingSubmitted(false);
    setQuizResetKey(prev => prev + 1);
  };

  /* ── SQL backend execution & result comparison (mirrors SqlEditor logic) ── */

  /** Clean a SQL query for execution: strip comments, take first valid query, add semicolon */
  const cleanSqlQuery = (raw: string): string => {
    let q = raw.trim();
    q = q.replace(/--.*$/gm, '');
    q = q.replace(/\/\*[\s\S]*?\*\//g, '');
    const parts = q.split(';').filter(p => p.trim().length > 0);
    if (parts.length > 0) {
      const first = parts.find(p => /^\s*(SELECT|WITH)/i.test(p.trim()));
      q = (first || parts[0]).trim();
    }
    if (!q.endsWith(';')) q += ';';
    return q;
  };

  /** Execute a SQL query against the backend */
  const sqlDatabase = pgCourse?.database || 'default';
  const executeQuery = async (query: string): Promise<{ success: boolean; columns?: string[]; data?: any[]; error?: string }> => {
    const token = localStorage.getItem('auth_token');
    if (!token) return { success: false, error: 'No estás autenticado. Iniciá sesión primero.' };

    const isProduction = window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1';
    const url = isProduction ? '/api/sql/execute.php' : 'http://localhost:8080/api/sql/execute.php';

    try {
      let response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ query, database: sqlDatabase }),
      });

      if (response.status === 401) {
        const fallbackUrl = isProduction
          ? `/api/sql/execute.php?token=${encodeURIComponent(token)}`
          : `http://localhost:8080/api/sql/execute.php?token=${encodeURIComponent(token)}`;
        response = await fetch(fallbackUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ query, database: sqlDatabase }),
        });
      }

      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        return { success: false, error: 'El servidor no respondió con JSON. Verificá que el backend PHP esté corriendo correctamente.' };
      }

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Error ejecutando la consulta');
      return data;
    } catch (err: any) {
      const msg = err?.message || '';
      if (msg.includes('Failed to fetch') || msg.includes('NetworkError') || err?.name === 'TypeError') {
        return { success: false, error: isProduction
          ? 'No se pudo conectar al servidor. Intentá de nuevo en unos segundos.'
          : 'No se pudo conectar al backend. Ejecutá: php -S localhost:8080 en la carpeta del proyecto.'
        };
      }
      return { success: false, error: msg || 'Error de conexión' };
    }
  };

  /** Compare two query results — same logic as SqlEditor */
  const compareResults = (
    userResult: { success: boolean; columns?: string[]; data?: any[] },
    solResult: { success: boolean; columns?: string[]; data?: any[] },
  ): { match: boolean; message: string } => {
    if (!userResult.success || !solResult.success) return { match: false, message: 'Error en alguna de las consultas.' };
    if (!userResult.data || !solResult.data) return { match: false, message: 'Faltan datos en los resultados.' };

    if (userResult.data.length !== solResult.data.length) {
      return { match: false, message: `Cantidad de filas incorrecta. Esperado: ${solResult.data.length}, Obtuviste: ${userResult.data.length}` };
    }

    const userCols = new Set(userResult.columns || []);
    const solCols = new Set(solResult.columns || []);
    if (userCols.size !== solCols.size) {
      return { match: false, message: `Cantidad de columnas incorrecta. Esperado: ${solCols.size}, Obtuviste: ${userCols.size}` };
    }
    for (const col of Array.from(solCols)) {
      if (!userCols.has(col)) return { match: false, message: `Falta la columna: ${col}` };
    }

    const normalizeRow = (row: any): string =>
      JSON.stringify(Object.keys(row).sort().map(k => ({ [k]: row[k] })));

    const solRows = new Set(solResult.data.map(normalizeRow));
    const userRows = new Set(userResult.data.map(normalizeRow));
    if (solRows.size !== userRows.size) return { match: false, message: 'Datos no coinciden. Revisá los valores devueltos.' };
    for (const row of Array.from(solRows)) {
      if (!userRows.has(row)) return { match: false, message: 'Los datos no coinciden con la solución esperada.' };
    }
    return { match: true, message: '¡Correcto! Tu consulta coincide con la solución.' };
  };

  /** Run SQL: execute both queries against backend and compare */
  const handleRunSql = async () => {
    setShowOutput(true);
    setValidation(null);
    setSqlTableData(null);
    setRunning(true);
    setOutput('Ejecutando query...');

    const userQuery = cleanSqlQuery(code);
    const solQuery = cleanSqlQuery(lesson.solutionCode);

    const [userResult, solResult] = await Promise.all([executeQuery(userQuery), executeQuery(solQuery)]);

    setRunning(false);

    if (!userResult.success) {
      setOutput(`❌ Error: ${userResult.error}`);
      setValidation({ passed: false, details: [`❌ ${userResult.error}`] });
      return;
    }

    const cols = userResult.columns || [];
    const rows = userResult.data || [];

    setSqlTableData({ columns: cols, rows: rows.slice(0, 50), totalRows: rows.length });
    setOutput('');

    if (!solResult.success) {
      return;
    }

    const comparison = compareResults(userResult, solResult);
    if (comparison.match) {
      setValidation({ passed: true, details: [`✅ ${comparison.message}`] });
    } else {
      setValidation({ passed: false, details: [`❌ ${comparison.message}`] });
    }
  };

  /** Run Python: execute user function against test cases using Function(); when no test cases, run code via API to show print output */
  const handleRunPython = async () => {
    setShowOutput(true);
    setValidation(null);

    const stripped = code.replace(/#.*$/gm, '').replace(/\s+/g, ' ').trim();
    if (!stripped) { setOutput('❌ Error: el editor está vacío.'); return; }

    const tests = lesson.testCases;
    if (!tests || tests.length === 0) {
      // No test cases — execute code via API to show real print() output, then validate by code comparison
      const normUser = code.split('\n').map(l => l.replace(/#.*$/, '')).join(' ').replace(/\s+/g, ' ').trim().replace(/\s/g, '');
      const normSol = lesson.solutionCode.split('\n').map(l => l.replace(/#.*$/, '')).join(' ').replace(/\s+/g, ' ').trim().replace(/\s/g, '');

      const token = localStorage.getItem('auth_token');
      const isProduction = window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1';
      const executeUrl = isProduction ? '/api/python/execute.php' : 'http://localhost:8080/api/python/execute.php';
      const body = JSON.stringify({ code: code.trim(), testCases: [], validateOnlyCode: false });

      let apiSuccess = false;
      if (token) {
        try {
          let res = await fetch(executeUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body,
          });
          // Si 401, reintentar con token en query (algunos servidores no envían Authorization en POST)
          if (res.status === 401) {
            const fallbackUrl = `${executeUrl}?token=${encodeURIComponent(token)}`;
            res = await fetch(fallbackUrl, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body,
            });
          }
          const data = await res.json().catch(() => ({}));
          if (res.ok && data.success && data.output !== undefined) {
            setOutput(data.output !== '' ? data.output : '(sin salida)');
            apiSuccess = true;
          } else {
            const errMsg = data.error || (res.status === 401 ? 'No autorizado. Iniciá sesión de nuevo.' : 'Error al ejecutar el código.');
            setOutput(errMsg);
          }
        } catch (e: any) {
          setOutput(e?.message || 'Error de conexión al ejecutar el código.');
        }
      } else {
        setOutput('Iniciá sesión para ejecutar el código.');
      }

      // Solo marcar ¡Correcto! si la API ejecutó bien Y el código coincide con la solución
      if (apiSuccess && normUser === normSol) {
        setValidation({ passed: true, details: ['✅ ¡Correcto!'] });
      } else if (!apiSuccess) {
        setValidation({ passed: false, details: ['❌ No se pudo ejecutar el código. Revisá el mensaje arriba.'] });
      } else {
        setValidation({ passed: false, details: ['❌ Tu código difiere de la solución esperada.'] });
      }
      return;
    }

    // Detect if code uses constructs the JS transpiler can't handle
    const needsServerExec = /\bclass\s+\w+|__init__|__str__|__repr__|__len__|__eq__|self\.|self,|self\)|\blambda\b|\bwith\b|^\s*@\w+/m.test(code)
      || /\bclass\s+\w+|__init__|self\./m.test(lesson.solutionCode);

    if (needsServerExec) {
      const token = localStorage.getItem('auth_token');
      const isProduction = window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1';
      const executeUrl = isProduction ? '/api/python/execute.php' : 'http://localhost:8080/api/python/execute.php';
      const body = JSON.stringify({ code: code.trim(), testCases: tests, validateOnlyCode: false });

      if (!token) { setOutput('Iniciá sesión para ejecutar el código.'); return; }

      try {
        let res = await fetch(executeUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body,
        });
        if (res.status === 401) {
          const fallbackUrl = `${executeUrl}?token=${encodeURIComponent(token)}`;
          res = await fetch(fallbackUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body,
          });
        }
        const data = await res.json().catch(() => ({}));

        if (!res.ok || !data.success) {
          const errMsg = data.error || 'Error al ejecutar el código.';
          setOutput(`❌ Error: ${errMsg}`);
          setValidation({ passed: false, details: [`❌ ${errMsg}`] });
          return;
        }

        const apiOutput = data.output ?? '';
        const testResults: any[] = data.testResults ?? [];

        if (testResults.length === 0) {
          setOutput(apiOutput || '(sin salida)');
          const normUser = code.split('\n').map((l: string) => l.replace(/#.*$/, '')).join(' ').replace(/\s+/g, ' ').trim().replace(/\s/g, '');
          const normSol = lesson.solutionCode.split('\n').map((l: string) => l.replace(/#.*$/, '')).join(' ').replace(/\s+/g, ' ').trim().replace(/\s/g, '');
          if (normUser === normSol) {
            setValidation({ passed: true, details: ['✅ ¡Correcto!'] });
          } else {
            setValidation({ passed: false, details: ['❌ Tu código difiere de la solución esperada.'] });
          }
          return;
        }

        const details: string[] = [];
        let allPassed = true;
        const outputLines: string[] = [];
        if (apiOutput) { outputLines.push(apiOutput); outputLines.push(''); }

        for (const tr of testResults) {
          const passed = !!tr.passed;
          const inputStr = tr.input ? Object.entries(tr.input).map(([k, v]: [string, any]) => `${k}=${JSON.stringify(v)}`).join(', ') : '';
          outputLines.push(`${tr.name}: (${inputStr})`);
          outputLines.push(`  Tu resultado:  ${JSON.stringify(tr.actualOutput)}`);
          outputLines.push(`  Esperado:      ${JSON.stringify(tr.expectedOutput)}`);
          outputLines.push(`  ${passed ? '✅ Correcto' : '❌ Incorrecto'}${tr.error ? ' — ' + tr.error : ''}`);
          outputLines.push('');

          if (passed) {
            details.push(`✅ ${tr.name}`);
          } else {
            details.push(`❌ ${tr.name}: obtuviste ${JSON.stringify(tr.actualOutput)}, esperado ${JSON.stringify(tr.expectedOutput)}${tr.error ? ' — ' + tr.error : ''}`);
            allPassed = false;
          }
        }

        setOutput(outputLines.join('\n'));
        if (allPassed) {
          details.unshift(`✅ ¡Todos los tests pasaron! (${testResults.length}/${testResults.length})`);
          setValidation({ passed: true, details });
        } else {
          const passedCount = details.filter(d => d.startsWith('✅')).length;
          details.unshift(`❌ ${passedCount}/${testResults.length} tests pasaron`);
          setValidation({ passed: false, details });
        }
      } catch (e: any) {
        setOutput(e?.message || 'Error de conexión al ejecutar el código.');
        setValidation({ passed: false, details: ['❌ Error de conexión.'] });
      }
      return;
    }

    // Simple functions: transpile Python-like code to JS for evaluation
    const funcMatch = lesson.solutionCode.match(/def\s+(\w+)\s*\(([^)]*)\)/);
    if (!funcMatch) {
      setOutput('⚠️ No se encontró la función a evaluar.');
      return;
    }
    const funcName = funcMatch[1];

    const runTestCases = (sourceCode: string): { results: any[]; error?: string } => {
      try {
        let js = sourceCode
          .replace(/#.*$/gm, '')
          .replace(/\bdef\s+/g, 'function ')
          .replace(/\bTrue\b/g, 'true')
          .replace(/\bFalse\b/g, 'false')
          .replace(/\bNone\b/g, 'null')
          .replace(/\belif\b/g, 'else if')
          .replace(/:\s*$/gm, ' {')
          .replace(/\bpass\b/g, '')
          .replace(/\band\b/g, '&&')
          .replace(/\bor\b/g, '||')
          .replace(/\bnot\b/g, '!')
          .replace(/\*\*/g, '**');

        const lines = js.split('\n');
        const processed: string[] = [];
        const indentStack: number[] = [];

        for (const line of lines) {
          const trimmed = line.trimEnd();
          if (!trimmed) continue;
          const indent = line.search(/\S/);
          if (indent < 0) continue;

          while (indentStack.length > 0 && indent <= indentStack[indentStack.length - 1]) {
            processed.push(' '.repeat(indentStack.pop()!) + '}');
          }
          if (trimmed.endsWith('{')) {
            indentStack.push(indent);
          }
          processed.push(trimmed);
        }
        while (indentStack.length > 0) {
          processed.push(' '.repeat(indentStack.pop()!) + '}');
        }

        const finalJs = processed.join('\n');

        const results: any[] = [];
        for (const tc of tests!) {
          const args = Object.values(tc.input);
          const argsStr = args.map(a => JSON.stringify(a)).join(', ');
          // eslint-disable-next-line no-new-func
          const fn = new Function(`${finalJs}\nreturn ${funcName}(${argsStr});`);
          results.push(fn());
        }
        return { results };
      } catch (err: any) {
        return { results: [], error: err.message || 'Error de ejecución' };
      }
    };

    const userRun = runTestCases(code);
    if (userRun.error) {
      setOutput(`❌ Error: ${userRun.error}`);
      setValidation({ passed: false, details: [`❌ ${userRun.error}`] });
      return;
    }

    const solRun = runTestCases(lesson.solutionCode);

    const details: string[] = [];
    let allPassed = true;
    const outputLines: string[] = [];

    for (let i = 0; i < tests.length; i++) {
      const tc = tests[i];
      const userVal = userRun.results[i];
      const expectedVal = solRun.error ? tc.expectedOutput : solRun.results[i];
      const inputStr = Object.entries(tc.input).map(([k, v]) => `${k}=${JSON.stringify(v)}`).join(', ');
      const passed = JSON.stringify(userVal) === JSON.stringify(expectedVal);

      outputLines.push(`${tc.name}: ${funcName}(${inputStr})`);
      outputLines.push(`  Tu resultado:  ${JSON.stringify(userVal)}`);
      outputLines.push(`  Esperado:      ${JSON.stringify(expectedVal)}`);
      outputLines.push(`  ${passed ? '✅ Correcto' : '❌ Incorrecto'}`);
      outputLines.push('');

      if (passed) {
        details.push(`✅ ${tc.name}: ${JSON.stringify(userVal)} === ${JSON.stringify(expectedVal)}`);
      } else {
        details.push(`❌ ${tc.name}: obtuviste ${JSON.stringify(userVal)}, esperado ${JSON.stringify(expectedVal)}`);
        allPassed = false;
      }
    }

    setOutput(outputLines.join('\n'));
    if (allPassed) {
      details.unshift(`✅ ¡Todos los tests pasaron! (${tests.length}/${tests.length})`);
      setValidation({ passed: true, details });
    } else {
      const passed = details.filter(d => d.startsWith('✅')).length;
      details.unshift(`❌ ${passed}/${tests.length} tests pasaron`);
      setValidation({ passed: false, details });
    }
  };

  const handleRun = () => {
    if (lang === 'text') return;
    if (lang === 'sql') handleRunSql();
    else handleRunPython();
  };

  /* ── Ranking helpers ── */
  const ranking = lesson.ranking;
  const hasRanking = lang === 'text' && !!ranking;

  const handleRankingClick = (optionId: number) => {
    if (rankingSubmitted) return;
    setUserRanking(prev => {
      if (prev.includes(optionId)) return prev.filter(id => id !== optionId);
      if (ranking && prev.length >= ranking.options.length) return prev;
      return [...prev, optionId];
    });
  };

  const handleRankingSubmit = () => {
    if (!ranking || userRanking.length !== ranking.options.length) return;
    setRankingSubmitted(true);
    if (JSON.stringify(userRanking) === JSON.stringify(ranking.correctOrder)) {
      handleMarkCompleted(currentIdx);
    }
  };

  const handleRankingReset = () => {
    if (rankingSubmitted) return;
    setUserRanking([]);
  };

  const getRankOf = (optionId: number): number | null => {
    const idx = userRanking.indexOf(optionId);
    return idx >= 0 ? idx + 1 : null;
  };

  const getRankLabel = (rank: number, total: number): string => {
    const ordinals: Record<number, string> = { 1: '1ro', 2: '2do', 3: '3ro', 4: '4to', 5: '5to' };
    const ordinal = ordinals[rank] || `${rank}°`;
    if (rank === 1) return `${ordinal} (Más acertada)`;
    if (rank === total) return `${ordinal} (Menos acertada)`;
    return ordinal;
  };

  const rankingIsCorrect = rankingSubmitted && ranking && JSON.stringify(userRanking) === JSON.stringify(ranking.correctOrder);

  const handleLoadSolution = () => {
    setCode(lesson.solutionCode);
    setLoadedSolution(true);
    setTimeout(() => setLoadedSolution(false), 2000);
  };

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(lesson.solutionCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* */ }
  };

  const loadExpectedResult = async () => {
    if (expectedResult || expectedLoading) return;
    if (lang !== 'sql') return;
    setExpectedLoading(true);
    setExpectedError(null);
    const solQuery = cleanSqlQuery(lesson.solutionCode);
    const result = await executeQuery(solQuery);
    setExpectedLoading(false);
    if (!result.success) {
      setExpectedError(result.error || 'Error al ejecutar la query esperada');
      return;
    }
    const cols = result.columns || [];
    const rows = result.data || [];
    setExpectedResult({ columns: cols, rows: rows.slice(0, 50), totalRows: rows.length });
  };

  const hasInterviewDownloads = lesson.downloads?.some(d => d.type === 'interview') ?? false;
  const hasGlossaryDownloads = lesson.downloads?.some(d => d.type === 'glossary') ?? false;

  const tabs: { key: Tab; label: string; icon: JSX.Element }[] = [
    { key: 'teoria', label: 'Teoría', icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" /></svg> },
    { key: 'ejercicio', label: 'Ejercicio', icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" /><path d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg> },
    { key: 'solucion', label: 'Solución', icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg> },
    ...(lang === 'sql' ? [{ key: 'resultado-esperado' as Tab, label: 'Resultado Esperado', icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" /></svg> }] : []),
    ...(hasInterviewDownloads ? [{ key: 'entrevista' as Tab, label: 'Entrevista', icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" /></svg> }] : []),
    ...(hasGlossaryDownloads ? [{ key: 'diccionario' as Tab, label: 'Diccionario', icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" /></svg> }] : []),
  ];

  const editorHtml = highlight(code, lang) + '\n';
  const solutionHtml = highlight(lesson.solutionCode, lang);

  return (
    <div className="flex h-screen flex-col bg-[#0d1117] text-white overflow-hidden">
      <style>{`
        .tok-comment { color: #6a737d; font-style: italic; }
        .tok-kw      { color: #ff7b72; }
        .tok-builtin { color: #79c0ff; }
        .tok-str     { color: #a5d6ff; }
        .tok-const   { color: #d2a8ff; }
        .tok-num     { color: #79c0ff; }
        .progress-bar-striped {
          background: repeating-linear-gradient(
            -55deg,
            #22c55e,
            #22c55e 10px,
            #16a34a 10px,
            #16a34a 20px
          );
          background-size: 200% 100%;
          animation: progress-shift 1.5s linear infinite;
        }
        @keyframes progress-shift {
          0% { background-position: 0 0; }
          100% { background-position: 40px 0; }
        }
      `}</style>

      {/* ── Top bar ── */}
      <header className="flex h-12 flex-shrink-0 items-center justify-between border-b border-gray-800 bg-[#161b22] px-4">
        <div className="flex items-center gap-3">
          <Link to={isBootcampRoute ? `/bootcamps/${pgCourse.courseId}${cohortSegment}` : `/cursos/${pgCourse.courseId}`} className="flex items-center gap-2 text-sm text-gray-400 hover:text-green-400 transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path d="M15 19l-7-7 7-7" /></svg>
            Volver al curso
          </Link>
          <span className="text-gray-700">|</span>
          <span className="text-sm font-semibold text-white">{pgCourse.courseTitle} — {lesson.title}</span>
        </div>
        <div className="flex items-center gap-4">
          <div className="hidden items-center gap-2 sm:flex">
            <div className="h-2 w-28 rounded-full bg-gray-800 overflow-hidden border border-gray-700/50">
              <div
                className={`h-full rounded-full transition-all duration-500 ${percentComplete(totalLessons) > 0 ? 'progress-bar-striped' : ''}`}
                style={{ width: `${percentComplete(totalLessons)}%` }}
              />
            </div>
            <span className="text-xs text-gray-500">{completedLessons.size}/{totalLessons}</span>
          </div>
          <span className="rounded bg-green-500/15 px-2 py-0.5 text-xs font-semibold text-green-400">{lesson.level}</span>
        </div>
      </header>

      {/* Theater mode overlay backdrop (click to close) */}
      {videoTheater && <div className="fixed inset-0 z-40 bg-black/80" onClick={() => setVideoTheater(false)} />}

      {/* ── Main split ── */}
      <div className="flex flex-1 overflow-hidden">
        {/* SCHEMA PANEL: Tables sidebar */}
        {lang === 'sql' && showSchema && (
          <div className="flex-shrink-0 w-48 border-r border-gray-800 bg-[#161b22] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-3 py-2 border-b border-gray-800">
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><ellipse cx="12" cy="5" rx="9" ry="3" /><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" /><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" /></svg>
                <span className="text-xs font-bold text-gray-300">Tablas</span>
              </div>
              <button onClick={() => setShowSchema(false)} className="text-gray-600 hover:text-gray-300 transition-colors" title="Ocultar tablas">
                <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-2 space-y-1">
              {schemaLoading ? (
                <div className="flex items-center justify-center py-6">
                  <svg className="h-5 w-5 animate-spin text-blue-400" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                </div>
              ) : schemaTables.length === 0 ? (
                <p className="text-xs text-gray-500 text-center py-4">Sin tablas</p>
              ) : (
                schemaTables.map((table) => {
                  const isExpanded = expandedSchemaTables.has(table.name);
                  return (
                    <div key={table.name} className="rounded border border-gray-700/50">
                      <button
                        onClick={() => toggleSchemaTable(table.name)}
                        className="w-full flex items-center gap-1.5 px-2 py-1.5 text-left hover:bg-gray-800/60 text-xs font-medium text-gray-300 transition-colors"
                      >
                        <svg className={`w-3 h-3 text-gray-500 transition-transform ${isExpanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path d="M9 5l7 7-7 7" /></svg>
                        <span className="font-mono text-green-400 truncate">{table.name}</span>
                      </button>
                      {isExpanded && (
                        <div className="border-t border-gray-700/50 bg-[#0d1117] py-1">
                          {table.columns.map((col) => (
                            <div
                              key={col.name}
                              className="px-4 py-0.5 text-xs font-mono text-gray-400 hover:text-gray-200 hover:bg-gray-800/40 cursor-default flex items-center justify-between"
                              title={col.type}
                            >
                              <span>{col.name}</span>
                              <span className="text-[10px] text-gray-600 ml-2">{(col.type || '').split('(')[0]}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* Toggle button when schema is hidden */}
        {lang === 'sql' && !showSchema && (
          <button
            onClick={() => setShowSchema(true)}
            className="flex-shrink-0 w-8 border-r border-gray-800 bg-[#161b22] flex items-center justify-center hover:bg-gray-800 transition-colors group"
            title="Mostrar tablas"
          >
            <div className="flex flex-col items-center gap-1">
              <svg className="w-4 h-4 text-gray-600 group-hover:text-blue-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><ellipse cx="12" cy="5" rx="9" ry="3" /><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" /><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" /></svg>
              <svg className="w-3 h-3 text-gray-600 group-hover:text-blue-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path d="M9 5l7 7-7 7" /></svg>
            </div>
          </button>
        )}

        {/* LEFT: Video + Editor or Ranking */}
        <div className="flex flex-1 min-w-0 flex-col border-r border-gray-800">
          {hasRanking && ranking ? (
            /* ── Ranking UI ── */
            <div className="flex flex-1 flex-col overflow-hidden">
              <div className="flex items-center justify-between border-b border-gray-800 bg-[#161b22] px-4 py-2">
                <div className="flex items-center gap-2">
                  <svg className="h-5 w-5 text-purple-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}><path d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" /></svg>
                  <span className="text-sm font-semibold text-gray-300">Ranking — Ordená de más acertada a menos</span>
                </div>
                <span className="text-xs text-gray-600">Hacé click en orden</span>
              </div>

              <div className="flex-1 overflow-y-auto p-5 space-y-4">
                <p className="text-sm text-gray-300 leading-relaxed">{ranking.question}</p>
                {!rankingSubmitted && (
                  <p className="text-xs text-gray-500">Hacé click en orden: primero la más acertada, después la segunda, y así hasta la menos acertada.</p>
                )}

                <div className="space-y-3">
                  {(shuffledOptions.length ? shuffledOptions : ranking.options).map((option) => {
                    const userRank = getRankOf(option.id);
                    const correctRank = ranking.correctOrder.indexOf(option.id) + 1;
                    const isRankCorrect = rankingSubmitted && userRank === correctRank;

                    return (
                      <div
                        key={option.id}
                        onClick={() => handleRankingClick(option.id)}
                        className={`relative p-4 rounded-xl border-2 transition-all duration-300 ${
                          rankingSubmitted
                            ? isRankCorrect
                              ? 'border-green-500/60 bg-green-500/10'
                              : 'border-red-500/40 bg-red-500/5'
                            : userRank
                            ? 'border-purple-500/60 bg-purple-500/10 cursor-pointer'
                            : 'border-gray-700 hover:border-purple-500/40 cursor-pointer hover:bg-gray-800/40'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${
                            rankingSubmitted
                              ? isRankCorrect
                                ? 'bg-green-500/20 text-green-400'
                                : 'bg-red-500/20 text-red-400'
                              : userRank
                              ? 'bg-purple-500/20 text-purple-400'
                              : 'bg-gray-800 text-gray-500'
                          }`}>
                            {userRank ? `#${userRank}` : '?'}
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              {rankingSubmitted && (
                                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                                  isRankCorrect
                                    ? 'bg-green-500/20 text-green-400'
                                    : 'bg-red-500/20 text-red-400'
                                }`}>
                                  {isRankCorrect
                                    ? `Correcto — ${getRankLabel(correctRank, ranking.options.length)}`
                                    : `Correcto: ${getRankLabel(correctRank, ranking.options.length)}`}
                                </span>
                              )}
                              {!rankingSubmitted && userRank && (
                                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-400">
                                  {getRankLabel(userRank, ranking.options.length)}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-300 leading-relaxed">{option.text}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Ranking feedback */}
                {rankingSubmitted && (
                  <div className={`p-4 rounded-lg border ${
                    rankingIsCorrect
                      ? 'bg-green-500/10 border-green-500/30'
                      : 'bg-red-500/5 border-red-500/20'
                  }`}>
                    <p className={`font-semibold mb-2 text-sm ${rankingIsCorrect ? 'text-green-400' : 'text-red-400'}`}>
                      {rankingIsCorrect ? '¡Ranking perfecto!' : 'El orden no era exacto.'}
                    </p>
                    <p className="text-sm text-gray-400 leading-relaxed">{ranking.explanation}</p>
                  </div>
                )}
              </div>

              {/* Ranking action bar */}
              <div className="flex items-center justify-between border-t border-gray-800 bg-[#161b22] px-4 py-2">
                {!rankingSubmitted ? (
                  <>
                    <div className="flex items-center gap-3">
                      {userRanking.length > 0 && (
                        <button onClick={handleRankingReset} className="text-xs text-gray-500 hover:text-gray-300 transition-colors">
                          Reiniciar
                        </button>
                      )}
                      <span className="text-xs text-gray-600">{userRanking.length}/{ranking.options.length} seleccionadas</span>
                    </div>
                    <button
                      onClick={handleRankingSubmit}
                      disabled={userRanking.length !== ranking.options.length}
                      className="flex items-center gap-2 rounded-md bg-purple-600 px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-purple-500 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                      Verificar Ranking
                    </button>
                  </>
                ) : (
                  <>
                    <span className="text-xs text-gray-500">
                      {rankingIsCorrect ? '¡Perfecto! Podés avanzar a la siguiente lección.' : 'Revisá la explicación y avanzá cuando estés listo.'}
                    </span>
                    <button onClick={() => { setActiveTab('solucion'); }} className="flex items-center gap-2 rounded-md bg-gray-700 px-5 py-2 text-sm font-semibold text-gray-300 transition-colors hover:bg-gray-600">
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                      Ver respuesta modelo
                    </button>
                  </>
                )}
              </div>
            </div>
          ) : (
            /* ── Standard Editor ── */
            <>
          {/* Video / DER Image — render when video fields exist (even if empty = pending) or DER image */}
          {(lesson.theoricClassVideoUrl !== undefined || lesson.groupClassVideoUrl !== undefined || pgCourse.derImage) && (
          <div className={videoTheater ? 'fixed inset-0 z-50 bg-black flex items-center justify-center' : `relative border-b border-gray-800 transition-all duration-500 ease-in-out ${videoCollapsed ? 'h-10 flex-shrink-0' : quizCollapsed ? 'flex-1 min-h-0' : 'h-[38%] flex-shrink-0'}`}>
            {(!videoCollapsed || videoTheater) ? (
              (lesson.theoricClassVideoUrl !== undefined || lesson.groupClassVideoUrl !== undefined) ? (
                <div className="h-full w-full bg-black flex flex-col" onContextMenu={e => e.preventDefault()}>
                  <div className="flex-1 flex items-center justify-center relative">
                  {(() => {
                    const url = (videoTab === 'grupal' ? lesson.groupClassVideoUrl : lesson.theoricClassVideoUrl) || lesson.theoricClassVideoUrl || '';
                    if (!url) return (
                      <div className="flex flex-col items-center justify-center gap-3 text-gray-500">
                        <svg className="w-12 h-12 opacity-40" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z" /></svg>
                        <span className="text-sm">Video no disponible aun</span>
                      </div>
                    );
                    const ytMatch = url.match(/youtube\.com\/embed\/([\w-]+)/);
                    if (url.includes('youtube.com')) return (
                      <>
                        <YouTubePlayer
                          key={url}
                          url={url}
                          title={`Clase: ${lesson.title}`}
                          courseId={courseId || ''}
                          lessonIndex={currentIdx}
                          onProgress80={() => { if (!completedLessons.has(currentIdx)) handleMarkCompleted(currentIdx); }}
                        />
                        {ytMatch && (
                          <a
                            href={`https://www.youtube.com/watch?v=${ytMatch[1]}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="absolute bottom-2 left-1/2 -translate-x-1/2 z-10 text-xs text-gray-500 hover:text-green-400 transition-colors bg-black/70 px-3 py-1 rounded-full backdrop-blur-sm"
                          >
                            ¿No carga el video? Abrilo en YouTube ↗
                          </a>
                        )}
                      </>
                    );
                    if (url.includes('vimeo.com')) return (
                      <iframe
                        key={url}
                        src={url}
                        title={`Clase: ${lesson.title}`}
                        className="h-full w-full"
                        allowFullScreen
                        allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
                      />
                    );
                    return null;
                  })() || (
                    <video
                      key={lesson.theoricClassVideoUrl}
                      className="h-full w-full object-contain"
                      controls
                      controlsList="nodownload noremoteplayback"
                      disablePictureInPicture
                      muted
                      playsInline
                      preload="metadata"
                      onContextMenu={e => e.preventDefault()}
                    >
                      <source src={lesson.theoricClassVideoUrl} type="video/mp4" />
                    </video>
                  )}
                  <div className={`absolute ${videoTheater ? 'top-4 right-4' : 'right-3 top-3'} z-10 flex items-center gap-1.5`}>
                    {videoTheater ? (
                      <button
                        onClick={() => setVideoTheater(false)}
                        className="flex items-center gap-2 rounded-lg bg-gray-800/90 px-3 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white transition-colors backdrop-blur-sm"
                        title="Salir del modo teatro (Esc)"
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25" /></svg>
                        Esc
                      </button>
                    ) : (
                      <>
                        <button onClick={() => setVideoTheater(true)} className="rounded bg-gray-800/80 p-1 text-gray-400 hover:bg-gray-700 hover:text-white transition-colors" title="Expandir video a pantalla completa">
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 4h-4m4 0l-5-5" /></svg>
                        </button>
                        <button onClick={() => setVideoCollapsed(true)} className="rounded bg-gray-800/80 p-1 text-gray-400 hover:bg-gray-700 hover:text-white transition-colors" title="Colapsar video">
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path d="M5 15l7-7 7 7" /></svg>
                        </button>
                      </>
                    )}
                  </div>
                  </div>
                </div>
              ) : pgCourse.derImage ? (
                <div className="h-full w-full overflow-auto bg-[#0d1117] flex items-center justify-center p-2">
                  <img
                    src={pgCourse.derImage}
                    alt="Esquema de Base de Datos"
                    className="max-h-full max-w-full object-contain rounded-lg border border-gray-700 cursor-zoom-in hover:border-green-500/50 transition-colors"
                    onClick={() => setDerExpanded(true)}
                    title="Click para expandir"
                  />
                  <button onClick={() => setDerExpanded(true)} className="absolute left-3 top-3 rounded bg-gray-800/80 p-1.5 text-gray-400 hover:bg-gray-700 hover:text-white transition-colors z-10" title="Expandir esquema">
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 4h-4m4 0l-5-5" /></svg>
                  </button>
                  <button onClick={() => setVideoCollapsed(true)} className="absolute right-3 top-3 rounded bg-gray-800/80 p-1 text-gray-400 hover:bg-gray-700 hover:text-white transition-colors z-10" title="Ocultar esquema">
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path d="M5 15l7-7 7 7" /></svg>
                  </button>
                </div>
              ) : (
                <div className="flex h-full w-full items-center justify-center bg-black/60">
                  <div className="flex flex-col items-center gap-3">
                    <div className="flex h-14 w-14 items-center justify-center rounded-full border-2 border-yellow-500/30 bg-yellow-500/10">
                      <svg className="h-6 w-6 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    </div>
                    <span className="text-sm font-semibold text-yellow-400">Próximamente</span>
                    <span className="text-xs text-gray-500">El video de esta lección estará disponible pronto</span>
                  </div>
                  <button onClick={() => setVideoCollapsed(true)} className="absolute right-3 top-3 rounded bg-gray-800/80 p-1 text-gray-400 hover:bg-gray-700 hover:text-white transition-colors" title="Colapsar video">
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path d="M5 15l7-7 7 7" /></svg>
                  </button>
                </div>
              )
            ) : (
              <button onClick={() => setVideoCollapsed(false)} className="flex h-full w-full items-center gap-2 px-4 text-xs text-gray-500 hover:text-green-400 transition-colors bg-[#161b22]">
                <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path d="M19 9l-7 7-7-7" /></svg>
                {(lesson.theoricClassVideoUrl || lesson.groupClassVideoUrl) ? 'Mostrar video' : pgCourse.derImage ? 'Mostrar esquema de BD' : 'Mostrar video'}
              </button>
            )}
          </div>
          )}

          {/* Video type selector */}
          {(lesson.theoricClassVideoUrl || lesson.groupClassVideoUrl) && !videoCollapsed && !videoTheater && (
            <div className="flex items-center gap-2 px-4 py-2 bg-[#0d1117] border-b border-gray-800">
              <button
                onClick={() => lesson.theoricClassVideoUrl ? setVideoTab('teorica') : undefined}
                disabled={!lesson.theoricClassVideoUrl}
                title={!lesson.theoricClassVideoUrl ? 'Video de clase teórica no disponible aún' : ''}
                className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-medium transition-all ${
                  videoTab === 'teorica'
                    ? 'bg-green-500/15 text-green-400 ring-1 ring-green-500/30 shadow-[0_0_8px_rgba(34,197,94,0.1)]'
                    : lesson.theoricClassVideoUrl
                      ? 'bg-gray-800/80 text-gray-400 hover:bg-gray-700/80 hover:text-gray-200 ring-1 ring-gray-700/50'
                      : 'bg-gray-800/40 text-gray-600 cursor-not-allowed ring-1 ring-gray-800/50'
                }`}
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" /></svg>
                Clase Teórica
              </button>
              <button
                onClick={() => lesson.groupClassVideoUrl ? setVideoTab('grupal') : undefined}
                disabled={!lesson.groupClassVideoUrl}
                title={!lesson.groupClassVideoUrl ? 'Video de clase grupal no disponible aún' : ''}
                className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-medium transition-all ${
                  videoTab === 'grupal'
                    ? 'bg-green-500/15 text-green-400 ring-1 ring-green-500/30 shadow-[0_0_8px_rgba(34,197,94,0.1)]'
                    : lesson.groupClassVideoUrl
                      ? 'bg-gray-800/80 text-gray-400 hover:bg-gray-700/80 hover:text-gray-200 ring-1 ring-gray-700/50'
                      : 'bg-gray-800/40 text-gray-600 cursor-not-allowed ring-1 ring-gray-800/50'
                }`}
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                Clase Grupal
              </button>
            </div>
          )}

          {/* Editor or Quiz */}
          <div className={`flex flex-col overflow-hidden transition-all duration-500 ease-in-out ${lesson.quiz && lesson.quiz.length > 0 && quizCollapsed ? 'h-10 flex-shrink-0' : 'flex-1'}`}>
            {lesson.quiz && lesson.quiz.length > 0 ? (
              /* ── Quiz Panel ── */
              <>
                <div
                  className={`flex items-center justify-between border-b border-gray-800 bg-[#161b22] px-4 py-2 ${quizCollapsed ? 'cursor-pointer hover:bg-gray-800/60' : ''} transition-colors`}
                  onClick={quizCollapsed ? () => setQuizCollapsed(false) : undefined}
                >
                  <div className="flex items-center gap-2">
                    <svg className="h-5 w-5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    <span className="text-sm font-semibold text-gray-300">{quizCollapsed ? 'Mostrar quiz' : 'Quiz'}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-gray-600">{lesson.quiz.length} preguntas</span>
                    <button
                      onClick={(e) => { e.stopPropagation(); setQuizCollapsed(prev => !prev); }}
                      className="rounded p-1 text-gray-500 hover:bg-gray-700 hover:text-gray-300 transition-colors"
                      title={quizCollapsed ? 'Expandir quiz' : 'Colapsar quiz para agrandar el video'}
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                        <path d={quizCollapsed ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'} />
                      </svg>
                    </button>
                  </div>
                </div>
                {!quizCollapsed && (
                  <div className="flex-1 overflow-auto p-4">
                    <QuizPanel key={quizResetKey} questions={lesson.quiz} onComplete={() => handleMarkCompleted(currentIdx)} />
                  </div>
                )}
              </>
            ) : (
              /* ── Standard Code Editor ── */
              <>
            <div className="flex items-center justify-between border-b border-gray-800 bg-[#161b22] px-4 py-2">
              <div className="flex items-center gap-2">
                {langInfo.icon}
                <span className="text-sm font-semibold text-gray-300">{langInfo.name}</span>
              </div>
              <span className="text-xs text-gray-600">Ctrl/Cmd + Enter para ejecutar</span>
            </div>

            {/* Code area */}
            <div className="relative flex-1 overflow-hidden bg-[#0d1117]">
              <div className="pointer-events-none absolute left-0 top-0 z-30 select-none py-4 font-mono text-sm leading-6 text-gray-600" style={{ width: '3rem' }}>
                {code.split('\n').map((_, i) => (<div key={i} className="pr-3 text-right">{i + 1}</div>))}
              </div>
              {lang !== 'text' && (
                <pre
                  ref={highlightRef}
                  className="pointer-events-none absolute inset-0 z-10 m-0 overflow-hidden whitespace-pre-wrap break-words py-4 font-mono text-sm leading-6 text-gray-300"
                  style={{ paddingLeft: '3.5rem', paddingRight: '1rem' }}
                  dangerouslySetInnerHTML={{ __html: editorHtml }}
                />
              )}
              <textarea
                ref={textareaRef}
                value={code}
                onChange={e => setCode(e.target.value)}
                onScroll={syncScroll}
                onKeyDown={e => { if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') { e.preventDefault(); handleRun(); } }}
                spellCheck={false}
                placeholder={lang === 'text' ? 'Escribí tu respuesta acá...' : undefined}
                className={`relative z-20 block h-full w-full resize-none border-0 bg-transparent py-4 ${lang === 'text' ? 'text-sm leading-6' : 'font-mono text-sm leading-6'} outline-none`}
                style={{ paddingLeft: '3.5rem', paddingRight: '1rem', color: lang === 'text' ? '#d1d5db' : 'transparent', caretColor: lang === 'text' ? '#d1d5db' : '#4ade80' }}
              />
            </div>

            {/* Run bar */}
            {lang !== 'text' ? (
              <div className="flex items-center justify-between border-t border-gray-800 bg-[#161b22] px-4 py-2">
                <span className="text-xs text-gray-600">Ctrl/Cmd + Enter para ejecutar</span>
                <button onClick={handleRun} disabled={running} className="flex items-center gap-2 rounded-md bg-green-600 px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-green-500 disabled:opacity-50 disabled:cursor-not-allowed">
                  {running ? (
                    <><svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>Ejecutando...</>
                  ) : (
                    <><svg className="h-4 w-4 fill-white" viewBox="0 0 24 24"><polygon points="6 3 20 12 6 21 6 3" /></svg>Ejecutar</>
                  )}
                </button>
              </div>
            ) : (
              <div className="flex items-center justify-between border-t border-gray-800 bg-[#161b22] px-4 py-2">
                <span className="text-xs text-gray-500">Usá este espacio para escribir tus respuestas</span>
                <button onClick={() => { setActiveTab('solucion'); handleLoadSolution(); }} className="flex items-center gap-2 rounded-md bg-purple-600 px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-purple-500">
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  Ver respuesta modelo
                </button>
              </div>
            )}
              </>
            )}

            {/* Output + Validation panel (resizable) */}
            {(showOutput || validation) && (
              <div className="flex-shrink-0 border-t border-gray-800 bg-[#0d1117] flex flex-col" style={{ height: outputHeight }}>
                {/* Drag handle */}
                <div
                  className="h-1.5 cursor-row-resize bg-gray-800 hover:bg-blue-500/50 active:bg-blue-500/70 transition-colors flex-shrink-0 group"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    isDraggingRef.current = true;
                    dragStartYRef.current = e.clientY;
                    dragStartHeightRef.current = outputHeight;
                    const onMove = (ev: MouseEvent) => {
                      if (!isDraggingRef.current) return;
                      const delta = dragStartYRef.current - ev.clientY;
                      setOutputHeight(Math.max(80, Math.min(600, dragStartHeightRef.current + delta)));
                    };
                    const onUp = () => {
                      isDraggingRef.current = false;
                      document.removeEventListener('mousemove', onMove);
                      document.removeEventListener('mouseup', onUp);
                    };
                    document.addEventListener('mousemove', onMove);
                    document.addEventListener('mouseup', onUp);
                  }}
                >
                  <div className="w-8 h-0.5 bg-gray-600 group-hover:bg-blue-400 rounded-full mx-auto mt-0.5 transition-colors" />
                </div>
                <div className="flex items-center justify-between border-b border-gray-800/50 bg-[#161b22] px-4 py-1.5 flex-shrink-0">
                  <span className="text-xs font-semibold text-gray-400">
                    {validation ? (validation.passed ? '✅ Resultado' : '❌ Resultado') : 'Output'}
                  </span>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setOutputHeight(h => Math.min(600, h + 100))} className="text-gray-600 hover:text-white" title="Expandir">
                      <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path d="M5 15l7-7 7 7" /></svg>
                    </button>
                    <button onClick={() => setOutputHeight(h => Math.max(80, h - 100))} className="text-gray-600 hover:text-white" title="Contraer">
                      <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path d="M19 9l-7 7-7-7" /></svg>
                    </button>
                    <button onClick={() => { setShowOutput(false); setValidation(null); setSqlTableData(null); setOutputHeight(208); }} className="text-gray-600 hover:text-white" title="Cerrar">
                      <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  </div>
                </div>
                <div className="flex-1 overflow-auto">

                {/* Validation details */}
                {validation && (
                  <div className={`px-4 py-3 space-y-1 border-b ${validation.passed ? 'border-green-500/20 bg-green-500/5' : 'border-red-500/20 bg-red-500/5'}`}>
                    {validation.details.map((d, i) => (
                      <div key={i} className={`text-sm font-mono ${d.startsWith('✅') ? 'text-green-400' : d.startsWith('❌') ? 'text-red-400' : d.startsWith('⚠️') ? 'text-yellow-400' : 'text-gray-400'}`}>
                        {d}
                      </div>
                    ))}
                  </div>
                )}

                {/* SQL table output */}
                {showOutput && sqlTableData && sqlTableData.columns.length > 0 && (
                  <div className="p-4">
                    <p className="text-xs text-gray-400 mb-2 font-mono">
                      Query ejecutada exitosamente ({sqlTableData.totalRows} fila{sqlTableData.totalRows !== 1 ? 's' : ''})
                    </p>
                    <div className="overflow-x-auto rounded border border-gray-700">
                      <table className="w-full text-sm font-mono">
                        <thead>
                          <tr className="bg-gray-800/80 border-b border-gray-700">
                            {sqlTableData.columns.map((col) => (
                              <th key={col} className="px-3 py-2 text-left text-xs font-bold text-blue-400 uppercase tracking-wider whitespace-nowrap">
                                {col}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {sqlTableData.rows.map((row, i) => (
                            <tr key={i} className={`border-b border-gray-800 ${i % 2 === 0 ? 'bg-[#0d1117]' : 'bg-[#161b22]'} hover:bg-gray-800/60 transition-colors`}>
                              {sqlTableData.columns.map((col) => (
                                <td key={col} className="px-3 py-1.5 text-gray-300 whitespace-nowrap">
                                  {row[col] === null ? <span className="text-gray-600 italic">NULL</span> : String(row[col])}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {sqlTableData.totalRows > sqlTableData.rows.length && (
                      <p className="text-xs text-gray-500 mt-2 font-mono">
                        Mostrando {sqlTableData.rows.length} de {sqlTableData.totalRows} filas
                      </p>
                    )}
                  </div>
                )}

                {/* Standard text output (Python, errors, etc.) */}
                {showOutput && output && !sqlTableData && (
                  <pre className="p-4 font-mono text-sm text-green-400 leading-6">{output}</pre>
                )}
                </div>
              </div>
            )}
          </div>
            </>
          )}
        </div>

        {/* RIGHT: Exercises */}
        <div className="flex flex-1 min-w-0 flex-col overflow-hidden">
          <div className="relative flex items-center justify-between border-b border-gray-800 bg-[#161b22] px-5 py-3">
            <div className="flex items-center gap-2">
              <svg className="h-5 w-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" /></svg>
              <span className="text-base font-bold text-white">Ejercicios</span>
            </div>
            <div className="flex items-center gap-3">
              {/* Complete / Reset lesson */}
              <button
                onClick={() => handleMarkCompleted(currentIdx)}
                disabled={completedLessons.has(currentIdx)}
                className={`flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold transition-all duration-300 ${
                  completedLessons.has(currentIdx)
                    ? 'bg-green-500/20 border border-green-500/40 text-green-400 cursor-default'
                    : 'bg-[#0d1117] border border-gray-600 text-gray-300 hover:border-green-500 hover:text-green-400 hover:bg-green-500/10'
                }`}
              >
                {completedLessons.has(currentIdx) ? (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}><path d="M5 13l4 4L19 7" /></svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><circle cx="12" cy="12" r="9" /><path d="M9 12l2 2 4-4" /></svg>
                )}
                {completedLessons.has(currentIdx) ? 'Completada' : 'Completar lección'}
              </button>
              {completedLessons.has(currentIdx) && (
                <button
                  onClick={handleResetLesson}
                  className="flex items-center gap-1.5 rounded-lg border border-gray-700 bg-[#0d1117] px-3 py-2 text-sm font-medium text-gray-400 transition-all duration-200 hover:border-amber-500/50 hover:text-amber-400 hover:bg-amber-500/10"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                  Reiniciar
                </button>
              )}

              {/* Compact pill — click to expand exercise grid */}
              <button
                onClick={() => setShowExerciseNav(prev => !prev)}
                className="flex items-center gap-2 rounded-full bg-gray-800 border border-gray-700 px-3 py-1.5 text-xs font-semibold transition-all hover:border-green-500/40 hover:bg-gray-700"
              >
                {completedLessons.size > 0 && (
                  <span className="flex items-center gap-1 text-green-400">
                    <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}><path d="M5 13l4 4L19 7" /></svg>
                    {completedLessons.size}
                  </span>
                )}
                <span className="text-gray-300">{currentIdx + 1}<span className="text-gray-600">/{totalLessons}</span></span>
                <svg className={`h-3 w-3 text-gray-500 transition-transform ${showExerciseNav ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path d="M19 9l-7 7-7-7" /></svg>
              </button>

              <div className="flex gap-1">
                <button onClick={() => goToLesson(Math.max(0, currentIdx - 1))} disabled={currentIdx === 0} className="rounded p-1 text-gray-400 hover:bg-gray-800 hover:text-white disabled:opacity-30 transition-colors">
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path d="M15 19l-7-7 7-7" /></svg>
                </button>
                {currentIdx === totalLessons - 1 && pgCourse.nextCourseId ? (
                  <Link to={`/cursos/${pgCourse.nextCourseId}/playground`} className="rounded p-1 text-green-400 hover:bg-gray-800 hover:text-green-300 transition-colors" title="Ir al siguiente curso">
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
                  </Link>
                ) : (
                  <button onClick={() => goToLesson(Math.min(totalLessons - 1, currentIdx + 1))} disabled={currentIdx === totalLessons - 1 || !isLessonAvailable(currentIdx + 1)} className="rounded p-1 text-gray-400 hover:bg-gray-800 hover:text-white disabled:opacity-30 transition-colors">
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path d="M9 5l7 7-7 7" /></svg>
                  </button>
                )}
              </div>
            </div>

            {/* Exercise grid dropdown */}
            {showExerciseNav && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setShowExerciseNav(false)} />
                <div className="absolute right-4 top-full z-40 mt-1 w-64 rounded-xl border border-gray-700 bg-[#161b22] p-3 shadow-2xl shadow-black/60">
                  <div className="flex items-center justify-between mb-2 px-1">
                    <span className="text-xs font-semibold text-gray-400">Ir a ejercicio</span>
                    <span className="text-[10px] text-gray-600">{completedLessons.size} completados</span>
                  </div>
                  <div className="grid grid-cols-6 gap-1.5">
                    {pgCourse.lessons.map((_, i) => {
                      const isCurrent = i === currentIdx;
                      const isDone = completedLessons.has(i);
                      const available = isLessonAvailable(i);
                      return (
                        <button
                          key={i}
                          onClick={() => { if (available) { goToLesson(i); setShowExerciseNav(false); } }}
                          disabled={!available}
                          title={!available ? `Disponible el ${pgCourse.lessons[i].availableFrom}` : undefined}
                          className={`flex items-center justify-center h-8 w-full rounded-lg text-xs font-bold transition-all ${
                            !available
                              ? 'bg-gray-800/40 text-gray-700 cursor-not-allowed'
                              : isCurrent
                              ? 'bg-green-500 text-black ring-2 ring-green-400/40'
                              : isDone
                              ? 'bg-green-500/15 text-green-400 hover:bg-green-500/25'
                              : 'bg-gray-800/80 text-gray-500 hover:bg-gray-700 hover:text-gray-300'
                          }`}
                        >
                          {!available
                            ? <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0110 0v4" /></svg>
                            : isDone && !isCurrent
                            ? <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}><path d="M5 13l4 4L19 7" /></svg>
                            : i + 1}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="flex border-b border-gray-800 bg-[#161b22]">
            {tabs.map(t => (
              <button key={t.key} onClick={() => { setActiveTab(t.key); if (t.key === 'resultado-esperado') loadExpectedResult(); }} className={`flex items-center gap-1.5 px-5 py-3 text-sm font-medium transition-colors ${activeTab === t.key ? 'border-b-2 border-green-400 text-green-400' : 'text-gray-500 hover:text-gray-300'}`}>
                {t.icon}
                {t.label}
              </button>
            ))}
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-6">
            {/* Descargas */}
            {/* Teoría */}
            {activeTab === 'teoria' && (
              <div className="space-y-5">
                <span className="inline-block rounded bg-green-500/15 px-2.5 py-1 text-xs font-bold tracking-wide text-green-400">{lesson.level}&ensp;#{currentIdx + 1}</span>
                <h2 className="text-2xl font-bold text-white">{lesson.theory.heading}</h2>
                <p className="text-sm leading-relaxed text-gray-400">{lesson.theory.intro}</p>
                {lesson.theory.sections.map((sec, i) => (
                  <div key={i} className="rounded-lg border border-gray-800 bg-[#161b22] p-5 space-y-3">
                    <h3 className="text-base font-bold text-white">{sec.title}</h3>
                    <div className="text-sm leading-relaxed text-gray-400 space-y-2">
                      {renderMarkdown(sec.body)}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Ejercicio */}
            {activeTab === 'ejercicio' && (
              <div className="space-y-5">
                <span className="inline-block rounded bg-yellow-500/15 px-2.5 py-1 text-xs font-bold tracking-wide text-yellow-400">EJERCICIO&ensp;#{currentIdx + 1}</span>
                <h2 className="text-2xl font-bold text-white">{lesson.exercise.heading}</h2>

                {/* PDF download card when available */}
                {lesson.downloads && lesson.downloads.filter(d => d.type === 'exercise').length > 0 && (
                  <div className="space-y-3">
                    {lesson.downloads.filter(d => d.type === 'exercise').map((dl, i) => (
                      <a key={i} href={dl.url} download target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 rounded-lg border border-yellow-500/20 bg-yellow-500/5 p-4 transition-all hover:border-yellow-500/40 hover:bg-yellow-500/10 group">
                        <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg bg-yellow-500/15">
                          <svg className="h-6 w-6 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m.75 12l3 3m0 0l3-3m-3 3v-6m-1.5-9H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" /></svg>
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-yellow-400 group-hover:text-yellow-300">{dl.label}</p>
                          <p className="text-xs text-gray-500">Click para descargar</p>
                        </div>
                        <svg className="h-5 w-5 text-gray-600 group-hover:text-yellow-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                      </a>
                    ))}
                  </div>
                )}

                {lesson.downloads && lesson.downloads.filter(d => d.type === 'dataset').length > 0 && (
                  <div className="space-y-3">
                    <p className="text-xs font-semibold uppercase tracking-wider text-cyan-400/70">Datasets</p>
                    {lesson.downloads.filter(d => d.type === 'dataset').map((dl, i) => (
                      <a key={i} href={dl.url} download target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 rounded-lg border border-cyan-500/20 bg-cyan-500/5 p-4 transition-all hover:border-cyan-500/40 hover:bg-cyan-500/10 group">
                        <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg bg-cyan-500/15">
                          <svg className="h-6 w-6 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M20.25 6.375c0 2.278-3.694 4.125-8.25 4.125S3.75 8.653 3.75 6.375m16.5 0c0-2.278-3.694-4.125-8.25-4.125S3.75 4.097 3.75 6.375m16.5 0v11.25c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125V6.375m16.5 0v3.75m-16.5-3.75v3.75m16.5 0v3.75C20.25 16.153 16.556 18 12 18s-8.25-1.847-8.25-4.125v-3.75m16.5 0c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125" /></svg>
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-cyan-400 group-hover:text-cyan-300">{dl.label}</p>
                          <p className="text-xs text-gray-500">Click para descargar</p>
                        </div>
                        <svg className="h-5 w-5 text-gray-600 group-hover:text-cyan-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                      </a>
                    ))}
                  </div>
                )}

                {((lesson.downloads?.filter(d => d.type === 'help').length ?? 0) > 0 || (lesson.exercise.instructions?.length ?? 0) > 0) && (
                  <div className="space-y-3">
                    <p className="text-xs font-semibold uppercase tracking-wider text-blue-400/70">Ayuda</p>
                    {lesson.downloads?.filter(d => d.type === 'help').map((dl, i) => {
                      const isExternal = dl.url.startsWith('http');
                      return (
                      <a key={`help-${i}`} href={dl.url} {...(!isExternal && { download: true })} target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 rounded-lg border border-blue-500/20 bg-blue-500/5 p-4 transition-all hover:border-blue-500/40 hover:bg-blue-500/10 group">
                        <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg bg-blue-500/15">
                          {isExternal ? (
                            <svg className="h-6 w-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" /></svg>
                          ) : (
                            <svg className="h-6 w-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" /></svg>
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-blue-400 group-hover:text-blue-300">{dl.label}</p>
                          <p className="text-xs text-gray-500">{isExternal ? 'Ver video tutorial' : 'Click para descargar'}</p>
                        </div>
                        {isExternal ? (
                          <svg className="h-5 w-5 text-gray-600 group-hover:text-blue-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" /></svg>
                        ) : (
                          <svg className="h-5 w-5 text-gray-600 group-hover:text-blue-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                        )}
                      </a>
                      );
                    })}
                    {lesson.exercise.instructions?.map((instr, i) => (
                      <div key={i} className="flex gap-3 items-start">
                        <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-green-500/15 text-xs font-bold text-green-400">{i + 1}</span>
                        <div className="text-sm text-gray-300 leading-relaxed" dangerouslySetInnerHTML={{ __html: renderMarkdownInline(instr) }} />
                      </div>
                    ))}
                  </div>
                )}

                {lesson.exercise.hint && (
                  <div className="rounded-lg border border-blue-500/20 bg-blue-500/5 px-4 py-3">
                    <p className="text-xs text-blue-300"><strong className="text-blue-400">Pista:</strong> {lesson.exercise.hint}</p>
                  </div>
                )}

                {hasRanking && (
                  <div className="rounded-lg border border-purple-500/20 bg-purple-500/5 px-4 py-3">
                    <p className="text-xs text-purple-300"><strong className="text-purple-400">Ejercicio interactivo:</strong> Usá el panel de ranking de la izquierda para ordenar las opciones de más acertada a menos acertada.</p>
                  </div>
                )}
              </div>
            )}

            {/* Resultado Esperado */}
            {activeTab === 'resultado-esperado' && (
              <div className="space-y-5">
                <div className="flex items-center gap-2">
                  <svg className="h-5 w-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" /></svg>
                  <h2 className="text-xl font-bold text-white">Resultado Esperado</h2>
                </div>
                <p className="text-sm text-gray-400">Así se ve el resultado correcto de este ejercicio. Tu query debe producir exactamente estas columnas y datos.</p>

                {expectedLoading && (
                  <div className="flex items-center justify-center py-12">
                    <div className="flex items-center gap-3">
                      <svg className="h-6 w-6 animate-spin text-blue-400" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                      <span className="text-sm text-gray-400">Ejecutando query de referencia...</span>
                    </div>
                  </div>
                )}

                {expectedError && (
                  <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-4">
                    <p className="text-sm text-red-400">❌ {expectedError}</p>
                    <button onClick={() => { setExpectedResult(null); setExpectedError(null); loadExpectedResult(); }} className="mt-2 text-xs text-red-300 underline hover:text-red-200">Reintentar</button>
                  </div>
                )}

                {expectedResult && (
                  <div className="space-y-3">
                    <div className="rounded-lg border border-blue-500/20 bg-blue-500/5 px-4 py-2">
                      <p className="text-xs text-blue-300">
                        <strong className="text-blue-400">{expectedResult.totalRows} filas</strong> · {expectedResult.columns.length} columnas: <span className="font-mono text-blue-200">{expectedResult.columns.join(', ')}</span>
                      </p>
                    </div>
                    <div className="overflow-x-auto rounded-lg border border-gray-700">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-gray-800/80 border-b border-gray-700">
                            {expectedResult.columns.map((col) => (
                              <th key={col} className="px-3 py-2 text-left text-xs font-bold text-blue-400 whitespace-nowrap font-mono">{col}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {expectedResult.rows.map((row, ri) => (
                            <tr key={ri} className={`border-b border-gray-800 ${ri % 2 === 0 ? 'bg-[#0d1117]' : 'bg-[#161b22]'} hover:bg-gray-800/60 transition-colors`}>
                              {expectedResult.columns.map((col) => (
                                <td key={col} className="px-3 py-1.5 text-gray-300 whitespace-nowrap font-mono">
                                  {row[col] === null ? <span className="text-gray-600 italic">NULL</span> : String(row[col])}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {expectedResult.totalRows > expectedResult.rows.length && (
                      <p className="text-xs text-gray-500 font-mono">Mostrando {expectedResult.rows.length} de {expectedResult.totalRows} filas</p>
                    )}
                  </div>
                )}

                <div className="rounded-lg border border-blue-500/20 bg-blue-500/5 px-4 py-3">
                  <p className="text-xs text-blue-200/70"><strong className="text-blue-400">Tip:</strong> Fijate en los nombres de las columnas y el orden de los datos. Tu query debe producir el mismo resultado, aunque el orden de las filas puede variar.</p>
                </div>
              </div>
            )}

            {/* Solución */}
            {activeTab === 'solucion' && (
              <div className="space-y-5">
                <div className="flex items-center gap-2">
                  <svg className="h-5 w-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  <h2 className="text-xl font-bold text-white">{lang === 'text' ? 'Respuesta Modelo' : 'Solución del Ejercicio'}</h2>
                </div>

                {/* PDF download card when available */}
                {lesson.downloads && lesson.downloads.filter(d => d.type === 'solution').length > 0 && (
                  <div className="space-y-3">
                    {lesson.downloads.filter(d => d.type === 'solution').map((dl, i) => {
                      const isExternal = dl.url.startsWith('http');
                      return (
                      <a key={i} href={dl.url} {...(!isExternal && { download: true })} target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 rounded-lg border border-green-500/20 bg-green-500/5 p-4 transition-all hover:border-green-500/40 hover:bg-green-500/10 group">
                        <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg bg-green-500/15">
                          {isExternal ? (
                            <svg className="h-6 w-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" /></svg>
                          ) : (
                            <svg className="h-6 w-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m.75 12l3 3m0 0l3-3m-3 3v-6m-1.5-9H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" /></svg>
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-green-400 group-hover:text-green-300">{dl.label}</p>
                          <p className="text-xs text-gray-500">{isExternal ? 'Ver en LinkedIn' : 'Click para descargar'}</p>
                        </div>
                        {isExternal ? (
                          <svg className="h-5 w-5 text-gray-600 group-hover:text-green-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" /></svg>
                        ) : (
                          <svg className="h-5 w-5 text-gray-600 group-hover:text-green-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                        )}
                      </a>);
                    })}
                  </div>
                )}

                {lesson.downloads && lesson.downloads.filter(d => d.type === 'help').length > 0 && (
                  <div className="space-y-3">
                    <p className="text-xs font-semibold uppercase tracking-wider text-blue-400/70">Recursos</p>
                    {lesson.downloads.filter(d => d.type === 'help').map((dl, i) => {
                      const isExternal = dl.url.startsWith('http');
                      return (
                      <a key={`sol-help-${i}`} href={dl.url} {...(!isExternal && { download: true })} target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 rounded-lg border border-blue-500/20 bg-blue-500/5 p-4 transition-all hover:border-blue-500/40 hover:bg-blue-500/10 group">
                        <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg bg-blue-500/15">
                          {isExternal ? (
                            <svg className="h-6 w-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" /></svg>
                          ) : (
                            <svg className="h-6 w-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" /></svg>
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-blue-400 group-hover:text-blue-300">{dl.label}</p>
                          <p className="text-xs text-gray-500">{isExternal ? 'Ver video tutorial' : 'Click para descargar'}</p>
                        </div>
                        {isExternal ? (
                          <svg className="h-5 w-5 text-gray-600 group-hover:text-blue-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" /></svg>
                        ) : (
                          <svg className="h-5 w-5 text-gray-600 group-hover:text-blue-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                        )}
                      </a>);
                    })}
                  </div>
                )}

                {lang === 'text' ? (
                  lesson.solutionCode ? (
                    <div className="rounded-lg border border-gray-800 bg-[#161b22] p-5 space-y-3">
                      <div className="text-sm leading-relaxed text-gray-400 space-y-2">
                        {renderMarkdown(lesson.solutionCode)}
                      </div>
                    </div>
                  ) : null
                ) : (
                  <div className="rounded-lg border border-gray-800 bg-[#0d1117] overflow-hidden">
                    <pre className="overflow-x-auto p-5 font-mono text-sm leading-7 text-gray-300" dangerouslySetInnerHTML={{ __html: solutionHtml }} />
                  </div>
                )}
                {lang !== 'text' && (
                  <div className="flex flex-col gap-3 sm:flex-row">
                    <button onClick={handleLoadSolution} className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-green-600 px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-green-500">
                      {loadedSolution ? (<><svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path d="M5 13l4 4L19 7" /></svg>Cargada</>) : (<><svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>Cargar Solución en Editor</>)}
                    </button>
                    <button onClick={handleCopyCode} className="flex items-center justify-center gap-2 rounded-lg border border-gray-700 bg-gray-800 px-5 py-3 text-sm font-medium text-gray-300 transition-colors hover:border-gray-600 hover:bg-gray-700 hover:text-white">
                      {copied ? (<><svg className="h-4 w-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path d="M5 13l4 4L19 7" /></svg><span className="text-green-400">Copiado!</span></>) : (<><svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" /></svg>Copiar Código</>)}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Preguntas de Entrevista */}
            {activeTab === 'entrevista' && (
              <div className="space-y-5">
                <div className="flex items-center gap-2">
                  <svg className="h-5 w-5 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" /></svg>
                  <h2 className="text-xl font-bold text-white">Preguntas de Entrevista</h2>
                </div>
                <p className="text-sm text-gray-400">Preguntas situacionales y conductuales basadas en los temas de esta semana. Practicá tus respuestas para entrevistas técnicas.</p>

                {lesson.downloads && lesson.downloads.filter(d => d.type === 'interview').length > 0 && (
                  <div className="space-y-3">
                    {lesson.downloads.filter(d => d.type === 'interview').map((dl, i) => (
                      <a key={i} href={dl.url} download target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 rounded-lg border border-orange-500/20 bg-orange-500/5 p-4 transition-all hover:border-orange-500/40 hover:bg-orange-500/10 group">
                        <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg bg-orange-500/15">
                          <svg className="h-6 w-6 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m.75 12l3 3m0 0l3-3m-3 3v-6m-1.5-9H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" /></svg>
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-orange-400 group-hover:text-orange-300">{dl.label}</p>
                          <p className="text-xs text-gray-500">Click para descargar</p>
                        </div>
                        <svg className="h-5 w-5 text-gray-600 group-hover:text-orange-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                      </a>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Diccionario de Palabras */}
            {activeTab === 'diccionario' && (
              <div className="space-y-5">
                <div className="flex items-center gap-2">
                  <svg className="h-5 w-5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" /></svg>
                  <h2 className="text-xl font-bold text-white">Diccionario de Palabras</h2>
                </div>
                <p className="text-sm text-gray-400">Glosario con todos los términos técnicos de esta semana explicados de forma simple, para que puedas consultarlo en cualquier momento.</p>

                {lesson.downloads && lesson.downloads.filter(d => d.type === 'glossary').length > 0 && (
                  <div className="space-y-3">
                    {lesson.downloads.filter(d => d.type === 'glossary').map((dl, i) => (
                      <a key={i} href={dl.url} download target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 rounded-lg border border-purple-500/20 bg-purple-500/5 p-4 transition-all hover:border-purple-500/40 hover:bg-purple-500/10 group">
                        <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg bg-purple-500/15">
                          <svg className="h-6 w-6 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m.75 12l3 3m0 0l3-3m-3 3v-6m-1.5-9H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" /></svg>
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-purple-400 group-hover:text-purple-300">{dl.label}</p>
                          <p className="text-xs text-gray-500">Click para descargar</p>
                        </div>
                        <svg className="h-5 w-5 text-gray-600 group-hover:text-purple-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                      </a>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Next course banner on last exercise */}
            {currentIdx === totalLessons - 1 && pgCourse.nextCourseId && (() => {
              const nextCourse = getCourseById(pgCourse.nextCourseId!);
              if (!nextCourse) return null;
              return (
                <div className="mt-8 rounded-xl border-2 border-green-500/30 bg-gradient-to-r from-green-500/10 via-emerald-500/5 to-green-500/10 p-6 space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-500/20 border border-green-500/30">
                      <svg className="h-6 w-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white">¡Completaste {pgCourse.courseTitle}!</h3>
                      <p className="text-sm text-gray-400">Último ejercicio — {totalLessons} de {totalLessons}</p>
                    </div>
                  </div>

                  <p className="text-sm text-gray-300 leading-relaxed">
                    Ahora es momento de poner a prueba todo lo que aprendiste. En el siguiente curso vas a resolver ejercicios de examen técnico con un modelo de datos real.
                  </p>

                  <div className="rounded-lg border border-gray-700 bg-[#161b22] p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br ${nextCourse.gradient} text-sm font-bold text-white`}>
                          {nextCourse.iconText}
                        </div>
                        <div>
                          <p className="text-sm font-bold text-white">{nextCourse.title}</p>
                          <p className="text-xs text-gray-500">{nextCourse.totalClasses} ejercicios · {nextCourse.level}</p>
                        </div>
                      </div>
                      <span className={`rounded px-2 py-0.5 text-xs font-bold ${
                        nextCourse.badgeColor === 'yellow' ? 'bg-yellow-500/15 text-yellow-400' :
                        nextCourse.badgeColor === 'green' ? 'bg-green-500/15 text-green-400' :
                        nextCourse.badgeColor === 'blue' ? 'bg-blue-500/15 text-blue-400' :
                        'bg-purple-500/15 text-purple-400'
                      }`}>{nextCourse.badge}</span>
                    </div>
                  </div>

                  <Link
                    to={`/cursos/${pgCourse.nextCourseId}/playground`}
                    className="flex items-center justify-center gap-2 rounded-lg bg-green-600 px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-green-500 w-full"
                  >
                    Ir al siguiente curso
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
                  </Link>
                </div>
              );
            })()}
          </div>
        </div>
      </div>

      {/* DER Fullscreen Modal */}
      {derExpanded && pgCourse.derImage && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90 backdrop-blur-sm"
          onClick={() => setDerExpanded(false)}
        >
          <div className="relative max-h-[95vh] max-w-[95vw] overflow-auto" onClick={e => e.stopPropagation()}>
            <img
              src={pgCourse.derImage}
              alt="Esquema de Base de Datos"
              className="max-h-[90vh] max-w-[90vw] object-contain rounded-lg border border-gray-600"
            />
            <button
              onClick={() => setDerExpanded(false)}
              className="absolute top-3 right-3 rounded-lg bg-gray-800/90 p-2 text-gray-300 hover:bg-gray-700 hover:text-white transition-colors"
              title="Cerrar"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
          <p className="absolute bottom-4 text-xs text-gray-500">Click fuera o presioná ESC para cerrar</p>
        </div>
      )}

      {showMetricsNudge && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={() => setShowMetricsNudge(false)}>
          <div className="mx-4 max-w-md rounded-2xl border border-green-500/30 bg-[#0d1117] p-6 shadow-2xl shadow-green-500/10" onClick={e => e.stopPropagation()}>
            <div className="mb-4 flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-500/15">
                <svg className="h-6 w-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" /></svg>
              </div>
              <h3 className="text-lg font-bold text-white">¡Lección completada!</h3>
            </div>
            <p className="mb-2 text-sm leading-relaxed text-gray-300">
              Los alumnos que registran sus métricas de posicionamiento <span className="font-semibold text-green-400">reciben 3x más contactos de reclutadores</span> que los que no lo hacen.
            </p>
            <p className="mb-5 text-sm text-gray-400">
              ¿Ya cargaste tus métricas de esta semana? Solo toma 2 minutos.
            </p>
            <div className="flex gap-3">
              <Link
                to="/mi-linkedin"
                className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-green-600 px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-green-500"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" /></svg>
                Cargar métricas
              </Link>
              <button
                onClick={() => setShowMetricsNudge(false)}
                className="rounded-lg border border-gray-700 px-4 py-2.5 text-sm font-medium text-gray-400 transition-colors hover:border-gray-600 hover:text-gray-300"
              >
                Después
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
